import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { organizationApi, authApi, authorizationApi, paymentsInfrastructureApi } from '@/lib/pillarApi';
import {
    getOrganizationCapacityLabel,
    organizationHasCapacity,
} from '@/lib/organization-capacity';
import { runInBackground } from '@/lib/utils';
import { editOrgSchema } from '@/lib/validation-organization';
import { useAuthStore } from '@/store/auth';
import { hasInternalAdminPrivileges, hasSuperadminPrivileges } from '@pillar-portal/api-types';
import type { ActiveRoleAssignmentResponse } from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { SecretsCard } from '@/components/secrets/SecretsCard';
import { PolicyPermissionBundlesCard } from '@/components/permissions/PolicyPermissionBundlesCard';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { appendCustomerOrganizationUuid } from '@/app/customer/customer-organization-search';
import {
    CustomerPortalDeliveryChannel,
    CustomerPortalPaymentRequestsConfigSchema,
    type DocumentTemplateSelector,
    getAllRoleAssignments,
    GetOrganizationResponse,
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    IpgSystemId,
    IpwSystemId,
    type BusinessDetails,
    type CanadaProvince,
    CanadaProvinceKeyList,
    type USState,
    BusinessDetailsSchema,
    formatPhoneNumber,
    isNormalizedPhoneNumberValid,
    normalizeCanadaPostalCode,
    normalizePhoneNumber,
    normalizeUSZipCode,
    USStateKeyList,
    type UserProfileDetailedPublicResponse,
    type MerchantAccountSearchListItem,
    GetOrganizationResponseSchema,
    IpgSystemIdKeyList,
    IpwSystemIdKeyList,
    type PlatformPaymentMethods,
    type PreviewOrganizationUsersCsvImportResponse,
    type OrganizationUserCsvImportPreviewRow,
    defaultIpgConfigByKey,
    defaultIpwConfigByKey,
    type CustomerPortalPaymentRequestsConfig,
    type IpwConfigDealertrack,
    normalizeNorthAmericanPhoneNumberToE164,
    type PaymentRequestDocumentPolicy,
    type PaymentRequestTemplateSelection,
    type OrganizationDocumentTemplateOverrideDescriptor,
    type OrganizationDocumentTemplateRegistryEntry,
    IpwConfigDealertrackSchema,
} from '@pillar-portal/api-types';
import { isAdminRoleAssignment } from '@pillar-portal/api-types';
import { formatOrganizationDisplayName } from '@/components/permissions/organizationDisplayName';

type EditOrgFormData = z.infer<typeof editOrgSchema>;
type OrganizationView = 'ISO' | 'MERCHANT' | 'CUSTOMER';

interface MerchantSystemOption<TSystemId extends number> {
    readonly systemId: TSystemId;
    readonly label: string;
}

export type PendingParentOrganizationAction = 'assign' | 'revoke';
type ParentOrganizationType = 'ISO' | 'MERCHANT';

export interface ParentOrganizationReassignConfirmationState {
    readonly showReassignConfirm: boolean;
    readonly pendingParentAction: PendingParentOrganizationAction | null;
    readonly pendingParentOrganizationName: string | null;
    readonly pendingParentOrganizationType: ParentOrganizationType | null;
}

interface BusinessDetailsEditorState {
    readonly country: 'US' | 'CA';
    readonly legalEntityName: string;
    readonly dba: string;
    readonly department: string;
    readonly mainPhoneNumber: string;
    readonly street: string;
    readonly street2: string;
    readonly city: string;
    readonly usState: USState | '';
    readonly usZip: string;
    readonly caProvince: CanadaProvince | '';
    readonly caPostalCode: string;
}

type DealertrackReconciliationEditorState = IpwConfigDealertrack['reconciliation'];

type TwilioSenderMode = 'from_phone_number' | 'messaging_service_sid';

interface CustomerPortalPaymentRequestsEditorState {
    readonly enabled: boolean;
    readonly allowEmail: boolean;
    readonly allowSms: boolean;
    readonly templateSelections: readonly PaymentRequestTemplateSelection[];
    readonly documentPolicy?: PaymentRequestDocumentPolicy;
    readonly twilioEnabled: boolean;
    readonly twilioSenderMode: TwilioSenderMode;
    readonly twilioFromPhoneNumber: string;
    readonly twilioMessagingServiceSid: string;
    readonly sendgridEnabled: boolean;
    readonly sendgridFromEmail: string;
    readonly sendgridFromName: string;
    readonly sendgridReplyToEmail: string;
}

interface MerchantPaymentMethodsEditorState {
    readonly terminal: boolean;
    readonly card: boolean;
    readonly ach: boolean;
    readonly e_invoice: boolean;
}

type PaymentRequestEmailCcEditorState = string[];

export interface DocumentTemplateEditorState {
    readonly selector: DocumentTemplateSelector;
    readonly overrideUuid: string | null;
    readonly defaultTemplateSource: string;
    readonly allowedRootVariables: readonly string[];
    readonly name: string;
    readonly description: string;
    readonly templateSource: string;
    readonly isActive: boolean;
}

const defaultBusinessDetailsEditorState: BusinessDetailsEditorState = {
    country: 'US',
    legalEntityName: '',
    dba: '',
    department: '',
    mainPhoneNumber: '',
    street: '',
    street2: '',
    city: '',
    usState: '',
    usZip: '',
    caProvince: '',
    caPostalCode: '',
};

const defaultCustomerPortalPaymentRequestsEditorState: CustomerPortalPaymentRequestsEditorState = {
    enabled: false,
    allowEmail: false,
    allowSms: false,
    templateSelections: [],
    twilioEnabled: false,
    twilioSenderMode: 'messaging_service_sid',
    twilioFromPhoneNumber: '',
    twilioMessagingServiceSid: '',
    sendgridEnabled: false,
    sendgridFromEmail: '',
    sendgridFromName: '',
    sendgridReplyToEmail: '',
};

const defaultPaymentRequestEmailCcEditorState: PaymentRequestEmailCcEditorState = [];
const defaultMerchantPaymentMethodsEditorState: MerchantPaymentMethodsEditorState = {
    terminal: false,
    card: false,
    ach: false,
    e_invoice: false,
};

export const PaymentRequestEmailCcEntrySchema: z.ZodType<string> = z
    .string()
    .trim()
    .email('Enter a valid email address');

const RECEIPT_FIELD_WARNING_TOOLTIP: string = 'This field is required to print payment receipts.';

function isActionableOrganizationUserCsvImportRow(
    row: OrganizationUserCsvImportPreviewRow
): boolean {
    return (
        (row.classification === 'insert' || row.classification === 'update')
        && (row.row_errors?.length ?? 0) === 0
    );
}

function getOrganizationUserCsvImportClassificationLabel(
    row: OrganizationUserCsvImportPreviewRow
): string {
    switch (row.classification) {
        case 'insert':
            return 'Insert';
        case 'update':
            return 'Update';
        case 'noop':
            return 'No-op';
        case 'csv_duplicate_error':
            return 'CSV Duplicate';
        case 'db_ambiguous_match_error':
            return 'Ambiguous Match';
        default:
            return row.classification;
    }
}

function isBlankOptionalField(value: string | undefined): boolean {
    return value === undefined || value.trim().length === 0;
}

function formatOptionalFieldValue(value: string | undefined): string {
    if (value === undefined || value.trim().length === 0) {
        return 'Not configured';
    }
    return value;
}

function hasBusinessDetailsCodeForTimezone(org: GetOrganizationResponse): boolean {
    if (org.businessDetails === undefined) {
        return false;
    }
    if (org.businessDetails.country === 'US') {
        return !isBlankOptionalField(org.businessDetails.address?.zip);
    }
    return !isBlankOptionalField(org.businessDetails.address?.postal_code);
}

function getMissingTimezoneCodeLabel(org: GetOrganizationResponse): 'ZIP' | 'Postal' {
    return org.businessDetails?.country === 'US' ? 'ZIP' : 'Postal';
}

function getOrganizationTimezoneDisplayValue(org: GetOrganizationResponse): string {
    if (org.detected_time_zone !== undefined) {
        return org.detected_time_zone;
    }
    if (!hasBusinessDetailsCodeForTimezone(org)) {
        return `Timezone unknown. Set ${getMissingTimezoneCodeLabel(org)} code`;
    }
    return 'Timezone unknown';
}

function renderOptionalReceiptFieldLabel(label: string, showWarning: boolean): ReactElement {
    return (
        <span className="inline-flex items-center gap-1">
            <span>{label}</span>
            {showWarning ? (
                <span title={RECEIPT_FIELD_WARNING_TOOLTIP} aria-label={RECEIPT_FIELD_WARNING_TOOLTIP}>
                    ⚠️
                </span>
            ) : null}
        </span>
    );
}

function serializeDocumentTemplateSelectorJson(
    selectorJson: DocumentTemplateSelector['selector_json']
): string {
    return JSON.stringify(selectorJson ?? null);
}

export function buildDocumentTemplateSelectorKey(
    selector: DocumentTemplateSelector
): string {
    return [
        selector.template_kind,
        selector.template_output_mime,
        selector.payload_type,
        selector.payload_discriminator,
        String(selector.payload_type_version),
        serializeDocumentTemplateSelectorJson(selector.selector_json),
    ].join(':');
}

export function buildDocumentTemplateEditorState(
    registryEntry: OrganizationDocumentTemplateRegistryEntry,
    override: OrganizationDocumentTemplateOverrideDescriptor | null
): DocumentTemplateEditorState {
    return {
        selector: registryEntry.selector,
        overrideUuid: override?.document_template_uuid ?? null,
        defaultTemplateSource: registryEntry.default_template_source,
        allowedRootVariables: registryEntry.allowed_root_variables,
        name: override?.name ?? registryEntry.name,
        description: override?.description ?? registryEntry.description ?? '',
        templateSource: override?.template_source ?? registryEntry.default_template_source,
        isActive: override?.is_active ?? true,
    };
}

export function formatDocumentTemplateSelectorSummary(
    selector: DocumentTemplateSelector
): string {
    return [
        selector.template_kind,
        selector.template_output_mime,
        `${selector.payload_type}:${selector.payload_discriminator}:v${selector.payload_type_version}`,
    ].join(' · ');
}

function buildDealertrackReconciliationEditorState(
    value: DealertrackReconciliationEditorState | undefined
): DealertrackReconciliationEditorState {
    if (value !== undefined) {
        return value;
    }
    return IpwConfigDealertrackSchema.parse(defaultIpwConfigByKey.DEALERTRACK).reconciliation;
}

const UsBusinessDetailsEditorSubmissionSchema = z.object({
    country: z.literal('US'),
    legalEntityName: z.string().trim().min(1, 'Legal entity name is required'),
    dba: z.string(),
    department: z.string(),
    mainPhoneNumber: z
        .string()
        .refine(
            (value) =>
                normalizePhoneNumber(value).length === 0 || isNormalizedPhoneNumberValid(value),
            'Main phone number must be 10 digits'
        ),
    street: z.string(),
    street2: z.string(),
    city: z.string(),
    usState: z.union([z.enum(USStateKeyList), z.literal('')]),
    usZip: z.string(),
});

const CaBusinessDetailsEditorSubmissionSchema = z.object({
    country: z.literal('CA'),
    legalEntityName: z.string().trim().min(1, 'Legal entity name is required'),
    dba: z.string(),
    department: z.string(),
    mainPhoneNumber: z
        .string()
        .refine(
            (value) =>
                normalizePhoneNumber(value).length === 0 || isNormalizedPhoneNumberValid(value),
            'Main phone number must be 10 digits'
        ),
    street: z.string(),
    street2: z.string(),
    city: z.string(),
    caProvince: z.union([z.enum(CanadaProvinceKeyList), z.literal('')]),
    caPostalCode: z.string(),
});

const BusinessDetailsEditorSubmissionSchema = z.discriminatedUnion('country', [
    UsBusinessDetailsEditorSubmissionSchema,
    CaBusinessDetailsEditorSubmissionSchema,
]);

function buildBusinessDetailsEditorState(
    businessDetails: OrganizationListItem['businessDetails'] | undefined
): BusinessDetailsEditorState {
    if (businessDetails === undefined) {
        return defaultBusinessDetailsEditorState;
    }
    if (businessDetails.country === 'US') {
        return {
            country: 'US',
            legalEntityName: businessDetails.legalEntityName,
            dba: businessDetails.dba ?? '',
            department: businessDetails.department ?? '',
            mainPhoneNumber: formatPhoneNumber(businessDetails.main_phone_number ?? ''),
            street: businessDetails.address?.street ?? '',
            street2: businessDetails.address?.street2 ?? '',
            city: businessDetails.address?.city ?? '',
            usState: businessDetails.address?.state ?? defaultBusinessDetailsEditorState.usState,
            usZip: businessDetails.address?.zip ?? '',
            caProvince: defaultBusinessDetailsEditorState.caProvince,
            caPostalCode: defaultBusinessDetailsEditorState.caPostalCode,
        };
    }
    return {
        country: 'CA',
        legalEntityName: businessDetails.legalEntityName,
        dba: businessDetails.dba ?? '',
        department: businessDetails.department ?? '',
        mainPhoneNumber: formatPhoneNumber(businessDetails.main_phone_number ?? ''),
        street: businessDetails.address?.street ?? '',
        street2: businessDetails.address?.street2 ?? '',
        city: businessDetails.address?.city ?? '',
        usState: defaultBusinessDetailsEditorState.usState,
        usZip: defaultBusinessDetailsEditorState.usZip,
        caProvince: businessDetails.address?.province ?? defaultBusinessDetailsEditorState.caProvince,
        caPostalCode: businessDetails.address?.postal_code ?? '',
    };
}

function buildBusinessDetailsPayloadOrThrow(
    editorState: BusinessDetailsEditorState
): BusinessDetails {
    const parseResult = BusinessDetailsEditorSubmissionSchema.safeParse(editorState);
    if (!parseResult.success) {
        throw new Error(parseResult.error.message);
    }
    const submissionData = parseResult.data;
    const normalizedDba: string | undefined =
        submissionData.dba.trim().length === 0 ? undefined : submissionData.dba.trim();
    const normalizedDepartment: string | undefined =
        submissionData.department.trim().length === 0 ? undefined : submissionData.department.trim();
    const normalizedMainPhoneNumberValue: string = normalizePhoneNumber(submissionData.mainPhoneNumber);
    const normalizedMainPhoneNumber: string | undefined =
        normalizedMainPhoneNumberValue.length === 0
            ? undefined
            : normalizedMainPhoneNumberValue;
    const normalizedStreet: string | undefined =
        submissionData.street.trim().length === 0 ? undefined : submissionData.street.trim();
    const normalizedStreet2: string | undefined =
        submissionData.street2.trim().length === 0 ? undefined : submissionData.street2.trim();
    const normalizedCity: string | undefined =
        submissionData.city.trim().length === 0 ? undefined : submissionData.city.trim();
    if (submissionData.country === 'US') {
        const normalizedUsZipValue: string = normalizeUSZipCode(submissionData.usZip);
        const normalizedUsZip: string | undefined =
            normalizedUsZipValue.length === 0 ? undefined : normalizedUsZipValue;
        const normalizedUsState: USState | undefined =
            submissionData.usState === '' ? undefined : submissionData.usState;
        const usAddressPayload: Record<string, unknown> = {
            ...(normalizedStreet !== undefined ? { street: normalizedStreet } : {}),
            ...(normalizedStreet2 !== undefined ? { street2: normalizedStreet2 } : {}),
            ...(normalizedCity !== undefined ? { city: normalizedCity } : {}),
            ...(normalizedUsState !== undefined ? { state: normalizedUsState } : {}),
            ...(normalizedUsZip !== undefined ? { zip: normalizedUsZip } : {}),
        };
        return BusinessDetailsSchema.parse({
            country: 'US',
            legalEntityName: submissionData.legalEntityName,
            ...(normalizedDba !== undefined ? { dba: normalizedDba } : {}),
            ...(normalizedDepartment !== undefined ? { department: normalizedDepartment } : {}),
            ...(normalizedMainPhoneNumber !== undefined
                ? { main_phone_number: normalizedMainPhoneNumber }
                : {}),
            ...(Object.keys(usAddressPayload).length > 0 ? { address: usAddressPayload } : {}),
        });
    }
    const normalizedCaProvince: CanadaProvince | undefined =
        submissionData.caProvince === '' ? undefined : submissionData.caProvince;
    const normalizedCaPostalCodeValue: string = normalizeCanadaPostalCode(submissionData.caPostalCode);
    const normalizedCaPostalCode: string | undefined =
        normalizedCaPostalCodeValue.length === 0
            ? undefined
            : normalizedCaPostalCodeValue;
    const caAddressPayload: Record<string, unknown> = {
        ...(normalizedStreet !== undefined ? { street: normalizedStreet } : {}),
        ...(normalizedStreet2 !== undefined ? { street2: normalizedStreet2 } : {}),
        ...(normalizedCity !== undefined ? { city: normalizedCity } : {}),
        ...(normalizedCaProvince !== undefined ? { province: normalizedCaProvince } : {}),
        ...(normalizedCaPostalCode !== undefined ? { postal_code: normalizedCaPostalCode } : {}),
    };
    return BusinessDetailsSchema.parse({
        country: 'CA',
        legalEntityName: submissionData.legalEntityName,
        ...(normalizedDba !== undefined ? { dba: normalizedDba } : {}),
        ...(normalizedDepartment !== undefined ? { department: normalizedDepartment } : {}),
        ...(normalizedMainPhoneNumber !== undefined ? { main_phone_number: normalizedMainPhoneNumber } : {}),
        ...(Object.keys(caAddressPayload).length > 0 ? { address: caAddressPayload } : {}),
    });
}

export function buildCustomerPortalPaymentRequestsEditorState(
    config: OrganizationListItem['customerPortalPaymentRequests'] | undefined
): CustomerPortalPaymentRequestsEditorState {
    const twilioConfig = config?.twilioSms;
    const sendgridConfig = config?.sendgridEmail;

    return {
        enabled: config?.enabled ?? defaultCustomerPortalPaymentRequestsEditorState.enabled,
        allowEmail:
            config?.allowedDeliveryChannels.includes(CustomerPortalDeliveryChannel.EMAIL) ??
            defaultCustomerPortalPaymentRequestsEditorState.allowEmail,
        allowSms:
            config?.allowedDeliveryChannels.includes(CustomerPortalDeliveryChannel.SMS) ??
            defaultCustomerPortalPaymentRequestsEditorState.allowSms,
        templateSelections:
            config?.templateSelections ??
            defaultCustomerPortalPaymentRequestsEditorState.templateSelections,
        documentPolicy: config?.documentPolicy,
        twilioEnabled:
            twilioConfig?.enabled ?? defaultCustomerPortalPaymentRequestsEditorState.twilioEnabled,
        twilioSenderMode:
            twilioConfig !== undefined && 'fromPhoneNumber' in twilioConfig
                ? 'from_phone_number'
                : defaultCustomerPortalPaymentRequestsEditorState.twilioSenderMode,
        twilioFromPhoneNumber:
            twilioConfig !== undefined && 'fromPhoneNumber' in twilioConfig
                ? formatPhoneNumber(twilioConfig.fromPhoneNumber ?? '')
                : defaultCustomerPortalPaymentRequestsEditorState.twilioFromPhoneNumber,
        twilioMessagingServiceSid:
            twilioConfig !== undefined && 'messagingServiceSid' in twilioConfig
                ? (twilioConfig.messagingServiceSid ?? '')
                : defaultCustomerPortalPaymentRequestsEditorState.twilioMessagingServiceSid,
        sendgridEnabled:
            sendgridConfig?.enabled ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridEnabled,
        sendgridFromEmail:
            sendgridConfig?.fromEmail ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridFromEmail,
        sendgridFromName:
            sendgridConfig?.fromName ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridFromName,
        sendgridReplyToEmail:
            sendgridConfig?.replyToEmail ??
            defaultCustomerPortalPaymentRequestsEditorState.sendgridReplyToEmail,
    };
}

export function buildMerchantPaymentMethodsEditorState(
    config: OrganizationListItem['merchantPaymentMethods'] | undefined
): MerchantPaymentMethodsEditorState {
    return {
        terminal: config?.terminal ?? defaultMerchantPaymentMethodsEditorState.terminal,
        card: config?.card ?? defaultMerchantPaymentMethodsEditorState.card,
        ach: config?.ach ?? defaultMerchantPaymentMethodsEditorState.ach,
        e_invoice: config?.e_invoice ?? defaultMerchantPaymentMethodsEditorState.e_invoice,
    };
}

function normalizePaymentRequestEmailCcValue(value: string): string {
    return value.trim().toLowerCase();
}

export function buildPaymentRequestEmailCcEditorState(
    emails: readonly string[] | undefined
): PaymentRequestEmailCcEditorState {
    if (emails === undefined) {
        return [...defaultPaymentRequestEmailCcEditorState];
    }
    const normalizedEmails: string[] = [];
    const seenNormalizedEmails = new Set<string>();
    for (const email of emails) {
        const normalizedEmail = normalizePaymentRequestEmailCcValue(email);
        if (normalizedEmail.length === 0 || seenNormalizedEmails.has(normalizedEmail)) {
            continue;
        }
        seenNormalizedEmails.add(normalizedEmail);
        normalizedEmails.push(normalizedEmail);
    }
    return normalizedEmails;
}

export function buildCustomerPortalPaymentRequestsPayloadOrThrow(
    editorState: CustomerPortalPaymentRequestsEditorState
): CustomerPortalPaymentRequestsConfig {
    const allowedDeliveryChannels: CustomerPortalDeliveryChannel[] = [];
    if (editorState.allowEmail) {
        allowedDeliveryChannels.push(CustomerPortalDeliveryChannel.EMAIL);
    }
    if (editorState.allowSms) {
        allowedDeliveryChannels.push(CustomerPortalDeliveryChannel.SMS);
    }

    if (editorState.allowSms && !editorState.twilioEnabled) {
        throw new Error('SMS delivery channel requires enabled Twilio sender settings');
    }
    if (editorState.allowEmail && !editorState.sendgridEnabled) {
        throw new Error('Email delivery channel requires enabled SendGrid sender settings');
    }

    const trimmedTwilioFromPhoneNumber = editorState.twilioFromPhoneNumber.trim();
    const normalizedTwilioFromPhoneNumber = normalizeNorthAmericanPhoneNumberToE164(
        trimmedTwilioFromPhoneNumber
    );
    const trimmedTwilioMessagingServiceSid = editorState.twilioMessagingServiceSid.trim();
    const trimmedSendgridFromEmail = editorState.sendgridFromEmail.trim();
    const trimmedSendgridFromName = editorState.sendgridFromName.trim();
    const trimmedSendgridReplyToEmail = editorState.sendgridReplyToEmail.trim();

    if (
        editorState.twilioEnabled &&
        editorState.twilioSenderMode === 'from_phone_number' &&
        trimmedTwilioFromPhoneNumber.length === 0
    ) {
        throw new Error('Twilio from phone number is required when SMS sender routing uses a phone number');
    }
    if (
        editorState.twilioEnabled &&
        editorState.twilioSenderMode === 'messaging_service_sid' &&
        trimmedTwilioMessagingServiceSid.length === 0
    ) {
        throw new Error(
            'Twilio messaging service SID is required when SMS sender routing uses a messaging service'
        );
    }
    if (editorState.sendgridEnabled && trimmedSendgridFromEmail.length === 0) {
        throw new Error('SendGrid from email is required when email delivery is enabled');
    }

    return CustomerPortalPaymentRequestsConfigSchema.parse({
        enabled: editorState.enabled,
        allowedDeliveryChannels,
        templateSelections: editorState.templateSelections,
        ...(editorState.documentPolicy !== undefined
            ? { documentPolicy: editorState.documentPolicy }
            : {}),
        ...(editorState.twilioEnabled
            ? editorState.twilioSenderMode === 'from_phone_number'
                ? {
                      twilioSms: {
                          enabled: true,
                          fromPhoneNumber: normalizedTwilioFromPhoneNumber,
                      },
                  }
                : {
                      twilioSms: {
                          enabled: true,
                          messagingServiceSid: trimmedTwilioMessagingServiceSid,
                      },
                  }
            : {}),
        ...(editorState.sendgridEnabled
            ? {
                  sendgridEmail: {
                      enabled: true,
                      fromEmail: trimmedSendgridFromEmail,
                      ...(trimmedSendgridFromName.length > 0
                          ? { fromName: trimmedSendgridFromName }
                          : {}),
                      ...(trimmedSendgridReplyToEmail.length > 0
                          ? { replyToEmail: trimmedSendgridReplyToEmail }
                          : {}),
                  },
              }
            : {}),
    });
}

export function getIsParentOrganizationReassignConfirmationModalOpen(
    state: ParentOrganizationReassignConfirmationState
): boolean {
    return (
        state.showReassignConfirm &&
        state.pendingParentAction !== null &&
        state.pendingParentOrganizationName !== null &&
        state.pendingParentOrganizationType !== null
    );
}

export function getParentOrganizationCandidatesByType(
    parentOrgType: ParentOrganizationType,
    isos: ReadonlyArray<OrganizationListItem>,
    merchants: ReadonlyArray<OrganizationListItem>
): ReadonlyArray<OrganizationListItem> {
    if (parentOrgType === 'ISO') {
        return isos;
    }
    if (parentOrgType === 'MERCHANT') {
        return merchants;
    }
    throw new Error(`Unsupported parent organization type: ${parentOrgType}`);
}

function isIsoOrganization(
    organization: GetOrganizationResponse | OrganizationListItem | null | undefined
): boolean {
    if (organization === null || organization === undefined) {
        return false;
    }
    return organizationHasCapacity(organization, 'is_iso');
}

function isMerchantOrganization(
    organization:
        | GetOrganizationResponse
        | OrganizationListItem
        | OrganizationOmnisearchListItem
        | null
        | undefined
): boolean {
    if (organization === null || organization === undefined) {
        return false;
    }
    return organizationHasCapacity(organization, 'is_merchant');
}

function getOrganizationView(
    organization: GetOrganizationResponse | OrganizationListItem,
    requestedView: string | null
): OrganizationView {
    if (requestedView === 'merchant' && isMerchantOrganization(organization)) {
        return 'MERCHANT';
    }
    if (requestedView === 'iso' && isIsoOrganization(organization)) {
        return 'ISO';
    }
    if (requestedView === 'customer' && organizationHasCapacity(organization, 'is_customer')) {
        return 'CUSTOMER';
    }
    if (isMerchantOrganization(organization)) {
        return 'MERCHANT';
    }
    if (isIsoOrganization(organization)) {
        return 'ISO';
    }
    if (organizationHasCapacity(organization, 'is_customer')) {
        return 'CUSTOMER';
    }
    throw new Error(
        `Organization ${organization.organization_uuid ?? '<unknown>'} has no configured capacities`
    );
}

export function getIpgSystemOptions(): ReadonlyArray<MerchantSystemOption<IpgSystemId>> {
    return IpgSystemIdKeyList.map((ipgSystemIdKey) => {
        const ipgConfig = defaultIpgConfigByKey[ipgSystemIdKey];
        const ipgSystemId: IpgSystemId = IpgSystemId[ipgSystemIdKey];
        return {
            systemId: ipgSystemId,
            label: ipgConfig.name,
        };
    });
}

export function getIpwSystemOptions(): ReadonlyArray<MerchantSystemOption<IpwSystemId>> {
    return IpwSystemIdKeyList.map((ipwSystemIdKey) => {
        const ipwConfig = defaultIpwConfigByKey[ipwSystemIdKey];
        const ipwSystemId: IpwSystemId = IpwSystemId[ipwSystemIdKey];
        return {
            systemId: ipwSystemId,
            label: ipwConfig.name,
        };
    });
}

export function appendUniqueSystemId<TSystemId extends number>(
    currentSystemIds: ReadonlyArray<TSystemId>,
    nextSystemId: TSystemId
): TSystemId[] {
    if (currentSystemIds.includes(nextSystemId)) {
        return [...currentSystemIds];
    }
    return [...currentSystemIds, nextSystemId];
}

export function removeSystemId<TSystemId extends number>(
    currentSystemIds: ReadonlyArray<TSystemId>,
    systemIdToRemove: TSystemId
): TSystemId[] {
    return currentSystemIds.filter((systemId) => systemId !== systemIdToRemove);
}

function renderOrganizationOmnisearchCard(
    organization: OrganizationOmnisearchListItem
): ReactElement {
    const businessDetailsSummary: string =
        organization.businessDetails === undefined
            ? 'Business details not set'
            : organization.businessDetails.legalEntityName;
    return (
        <div className="space-y-1">
            <div className="font-medium">{organization.name}</div>
            <div className="text-xs text-muted-foreground">
                Capacities: {getOrganizationCapacityLabel(organization)} | UUID:{' '}
                {organization.organization_uuid}
            </div>
            {isMerchantOrganization(organization) && (
                <div className="text-xs text-muted-foreground">{businessDetailsSummary}</div>
            )}
        </div>
    );
}

function formatMerchantAccountSummaryLine(merchantAccount: MerchantAccountSearchListItem): string {
    if (merchantAccount.allow_surcharge) {
        return `MID: ${merchantAccount.processor_mid}, surcharge $${merchantAccount.surcharge_pct}%`;
    }
    return `MID: ${merchantAccount.processor_mid}, no surcharge`;
}

function getSystemOptionLabelOrThrow<TSystemId extends number>(
    systemOptions: ReadonlyArray<MerchantSystemOption<TSystemId>>,
    systemId: TSystemId
): string {
    const matchedOption = systemOptions.find((option) => option.systemId === systemId);
    if (matchedOption === undefined) {
        throw new Error(`Unknown system id: ${systemId}`);
    }
    return matchedOption.label;
}

const CursorPageParamSchema = z.string().trim().min(1);

function parseEmbeddedListCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    return CursorPageParamSchema.parse(pageParam);
}

export function OrganizationDetailContent() {
    const log = createComponentLogger('OrganizationDetailContent');
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuidParseResult = z.string().uuid().safeParse(params.id);
    if (!organizationUuidParseResult.success) {
        throw new Error(`Invalid organization UUID: ${params.id}`);
    }
    const organizationUuidFromRoute = organizationUuidParseResult.data;
    const queryClient = useQueryClient();
    const bumpOrganizationListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpOrganizationListsVersion
    );
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [isReassigning, setIsReassigning] = useState(false);
    const [isAssigningParentMerchant, setIsAssigningParentMerchant] = useState<boolean>(false);
    const [isEditingConfig, setIsEditingConfig] = useState(false);
    const [isEditingBusinessDetails, setIsEditingBusinessDetails] = useState<boolean>(false);
    const [businessDetailsEditorError, setBusinessDetailsEditorError] = useState<string | null>(null);
    const [configEditorError, setConfigEditorError] = useState<string | null>(null);
    const [businessDetailsEditorState, setBusinessDetailsEditorState] =
        useState<BusinessDetailsEditorState>(defaultBusinessDetailsEditorState);
    const [enabledIpgSystemIdsForEdit, setEnabledIpgSystemIdsForEdit] = useState<IpgSystemId[]>([]);
    const [enabledIpwSystemIdsForEdit, setEnabledIpwSystemIdsForEdit] = useState<IpwSystemId[]>([]);
    const [dealertrackReconciliationForEdit, setDealertrackReconciliationForEdit] =
        useState<DealertrackReconciliationEditorState>(
            buildDealertrackReconciliationEditorState(undefined)
        );
    const [customerPortalPaymentRequestsForEdit, setCustomerPortalPaymentRequestsForEdit] =
        useState<CustomerPortalPaymentRequestsEditorState>(
            defaultCustomerPortalPaymentRequestsEditorState
        );
    const [merchantPaymentMethodsForEdit, setMerchantPaymentMethodsForEdit] =
        useState<MerchantPaymentMethodsEditorState>(defaultMerchantPaymentMethodsEditorState);
    const [merchantPaymentConfirmationEmailCcForEdit, setMerchantPaymentConfirmationEmailCcForEdit] =
        useState<PaymentRequestEmailCcEditorState>(defaultPaymentRequestEmailCcEditorState);
    const [merchantPaymentConfirmationEmailCcInput, setMerchantPaymentConfirmationEmailCcInput] =
        useState('');
    const [merchantPaymentConfirmationEmailCcInputError, setMerchantPaymentConfirmationEmailCcInputError] =
        useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showReassignConfirm, setShowReassignConfirm] = useState(false);
    const [pendingParentOrganizationUuid, setPendingParentOrganizationUuid] = useState<
        string | null
    >(null);
    const [pendingParentOrganizationName, setPendingParentOrganizationName] = useState<
        string | null
    >(null);
    const [pendingParentOrganizationType, setPendingParentOrganizationType] = useState<
        ParentOrganizationType | null
    >(null);
    const [pendingParentAction, setPendingParentAction] =
        useState<PendingParentOrganizationAction | null>(null);
    const [showImportUsersDialog, setShowImportUsersDialog] = useState(false);
    const [organizationUsersImportCsvText, setOrganizationUsersImportCsvText] = useState('');
    const [organizationUsersImportFileName, setOrganizationUsersImportFileName] = useState<
        string | null
    >(null);
    const [organizationUsersImportPreview, setOrganizationUsersImportPreview] =
        useState<PreviewOrganizationUsersCsvImportResponse | null>(null);
    const [selectedOrganizationUsersImportRowIndexes, setSelectedOrganizationUsersImportRowIndexes] =
        useState<number[]>([]);
    const [organizationUsersImportError, setOrganizationUsersImportError] =
        useState<string | null>(null);
    const user = useAuthStore((state) => state.user);
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    useEffect(() => {
        if (showReassignConfirm) {
            return;
        }
        setPendingParentOrganizationUuid(null);
        setPendingParentOrganizationName(null);
        setPendingParentOrganizationType(null);
        setPendingParentAction(null);
    }, [showReassignConfirm]);

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000, // Cache for 30 seconds
    });
    const canManageOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );
    const canAuditPermissions: boolean = isAdminRoleAssignment(
        activeRoleAssignment ?? null
    );

    useEffect(() => {
        if (canManageOrganizations) {
            return;
        }
        setIsReassigning(false);
        setIsAssigningParentMerchant(false);
    }, [canManageOrganizations]);

    const {
        data: org,
        isLoading,
        error,
    } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
        ]),
        queryFn: async () => {
            const response = await organizationApi.getOrganization(organizationUuidFromRoute);
            return GetOrganizationResponseSchema.parse(response);
        },
    });

    const organizationUuid: string | undefined = org?.organization_uuid;

    // Check if user has access to this organization (for config editing)
    const { data: canEditConfigData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'has_access_to_organization',
            organizationUuid,
        ]),
        queryFn: async () => {
            if (organizationUuid === undefined) {
                return false;
            }
            const response = await authorizationApi.hasAccessToOrganization(organizationUuid);
            return response.has_access;
        },
        enabled: organizationUuid !== undefined,
    });

    const canEditConfig: boolean = canManageOrganizations && (canEditConfigData ?? false);

    const resetOrganizationUsersImportDialog = (): void => {
        setShowImportUsersDialog(false);
        setOrganizationUsersImportCsvText('');
        setOrganizationUsersImportFileName(null);
        setOrganizationUsersImportPreview(null);
        setSelectedOrganizationUsersImportRowIndexes([]);
        setOrganizationUsersImportError(null);
    };

    const previewOrganizationUsersCsvImportMutation = useMutation({
        mutationFn: async (csvText: string) => {
            return await organizationApi.previewOrganizationUsersCsvImport(
                organizationUuidFromRoute,
                {
                    csv_text: csvText,
                }
            );
        },
        onSuccess: (response) => {
            setOrganizationUsersImportPreview(response);
            setSelectedOrganizationUsersImportRowIndexes([]);
            setOrganizationUsersImportError(null);
        },
        onError: (error) => {
            setOrganizationUsersImportPreview(null);
            setSelectedOrganizationUsersImportRowIndexes([]);
            setOrganizationUsersImportError(
                error instanceof Error ? error.message : 'Failed to preview imported users'
            );
        },
    });

    const applyOrganizationUsersCsvImportMutation = useMutation({
        mutationFn: async (selectedRowIndexes: number[]) => {
            return await organizationApi.applyOrganizationUsersCsvImport(
                organizationUuidFromRoute,
                {
                    csv_text: organizationUsersImportCsvText,
                    selected_row_indexes: selectedRowIndexes,
                }
            );
        },
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'users',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization users after CSV apply:', err);
                }
            );
            resetOrganizationUsersImportDialog();
        },
        onError: (error) => {
            setOrganizationUsersImportError(
                error instanceof Error ? error.message : 'Failed to apply imported users'
            );
        },
    });

    const actionableOrganizationUsersImportRows = useMemo(() => {
        return organizationUsersImportPreview?.rows.filter(
            isActionableOrganizationUserCsvImportRow
        ) ?? [];
    }, [organizationUsersImportPreview]);
    const actionableOrganizationUsersImportRowIndexes = useMemo(() => {
        return actionableOrganizationUsersImportRows.map((row) => row.row_index);
    }, [actionableOrganizationUsersImportRows]);
    const selectedOrganizationUsersImportRowIndexSet = useMemo(() => {
        return new Set(selectedOrganizationUsersImportRowIndexes);
    }, [selectedOrganizationUsersImportRowIndexes]);
    const areAllActionableOrganizationUsersImportRowsSelected =
        actionableOrganizationUsersImportRowIndexes.length > 0
        && actionableOrganizationUsersImportRowIndexes.every((rowIndex) =>
            selectedOrganizationUsersImportRowIndexSet.has(rowIndex)
        );

    useEffect(() => {
        const requestedEditMode: string | null = searchParams.get('edit');
        if (requestedEditMode !== 'business-details') {
            return;
        }
        if (!canManageOrganizations || org === undefined || org.status === 'DELETED') {
            return;
        }
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(org.businessDetails));
        setIsEditingBusinessDetails(true);
    }, [searchParams, canManageOrganizations, org]);
    const merchantAccountsForOrganizationQuery = useQuery<
        ReadonlyArray<MerchantAccountSearchListItem>
    >({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
            'merchant_accounts',
        ]),
        queryFn: async () => {
            const merchantAccountsSearchResponse =
                await paymentsInfrastructureApi.searchMerchantAccounts({
                    merchant_organization_uuid: organizationUuid,
                    limit: 100,
                });
            return merchantAccountsSearchResponse.merchant_accounts;
        },
        enabled: !!org && isMerchantOrganization(org) && organizationUuid !== undefined,
    });

    const { data: parentOrganizationData } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            org?.parent_organization_uuid,
        ]),
        queryFn: async () => {
            if (!org?.parent_organization_uuid) {
                throw new Error('Parent organization UUID is required');
            }
            const response = await organizationApi.getOrganization(org.parent_organization_uuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: !!org?.parent_organization_uuid,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<EditOrgFormData>({
        resolver: zodResolver(editOrgSchema),
        ...(org && { values: { name: org.name, status: org.status } }),
    });

    const updateMutation = useMutation({
        mutationFn: (data: EditOrgFormData) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                ...data,
                status: data.status,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setIsEditing(false);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.deleteOrganization(org.organization_uuid);
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const undeleteMutation = useMutation({
        mutationFn: () => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.undeleteOrganization(org.organization_uuid);
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const setParentOrganizationMutation = useMutation({
        mutationFn: (parentOrganizationUuid: string | null) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.setOrganizationParent({
                organization_uuid: org.organization_uuid,
                parent_organization_uuid: parentOrganizationUuid,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'subsidiary_isos',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'subsidiary_merchants',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                        'users',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setIsReassigning(false);
            setShowReassignConfirm(false);
        },
    });

    const updateConfigMutation = useMutation({
        mutationFn: (updatePayload: {
            readonly enable_ipg_system_ids: IpgSystemId[];
            readonly enable_ipw_system_ids: IpwSystemId[];
            readonly dealertrackReconciliation?: DealertrackReconciliationEditorState;
            readonly merchantPaymentMethods?: PlatformPaymentMethods;
            readonly customerPortalPaymentRequests?: CustomerPortalPaymentRequestsConfig;
            readonly merchantPaymentConfirmationEmailCc?: string[];
        }) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                enable_ipg_system_ids: updatePayload.enable_ipg_system_ids,
                enable_ipw_system_ids: updatePayload.enable_ipw_system_ids,
                dealertrackReconciliation: updatePayload.dealertrackReconciliation,
                merchantPaymentMethods: updatePayload.merchantPaymentMethods,
                customerPortalPaymentRequests: updatePayload.customerPortalPaymentRequests,
                merchantPaymentConfirmationEmailCc:
                    updatePayload.merchantPaymentConfirmationEmailCc,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setConfigEditorError(null);
            setIsEditingConfig(false);
        },
    });

    const updateBusinessDetailsMutation = useMutation({
        mutationFn: (businessDetails: BusinessDetails) => {
            if (!org?.organization_uuid) {
                throw new Error('Organization UUID is required');
            }
            return organizationApi.updateOrganization(org.organization_uuid, {
                businessDetails: businessDetails,
            });
        },
        onSuccess: () => {
            bumpOrganizationListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'organization',
                        organizationUuidFromRoute,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization query after business details update:', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['organizations'] }),
                (err) => {
                    log.error('Error invalidating organizations list after business details update:', err);
                }
            );
            setBusinessDetailsEditorError(null);
            setIsEditingBusinessDetails(false);
        },
    });

    const exportMerchantBackupMutation = useMutation({
        mutationFn: async () => await organizationApi.exportMerchantBackup(organizationUuidFromRoute),
        onSuccess: (response) => {
            const backupPayloadJson: string = JSON.stringify(response.backup, null, 2);
            const backupFileBlob: Blob = new Blob([backupPayloadJson], {
                type: 'application/json',
            });
            const backupDownloadUrl: string = URL.createObjectURL(backupFileBlob);
            const backupDownloadAnchor: HTMLAnchorElement = document.createElement('a');
            backupDownloadAnchor.href = backupDownloadUrl;
            backupDownloadAnchor.download =
                `merchant-backup-${organizationUuidFromRoute}.json`;
            backupDownloadAnchor.click();
            URL.revokeObjectURL(backupDownloadUrl);
        },
    });

    const handleEditConfig = (organization: GetOrganizationResponse | OrganizationListItem) => {
        setConfigEditorError(null);
        setEnabledIpgSystemIdsForEdit(organization.enable_ipg_system_ids ?? []);
        setEnabledIpwSystemIdsForEdit(organization.enable_ipw_system_ids ?? []);
        setDealertrackReconciliationForEdit(
            buildDealertrackReconciliationEditorState(organization.dealertrackReconciliation)
        );
        setMerchantPaymentMethodsForEdit(
            buildMerchantPaymentMethodsEditorState(organization.merchantPaymentMethods)
        );
        setCustomerPortalPaymentRequestsForEdit(
            buildCustomerPortalPaymentRequestsEditorState(
                organization.customerPortalPaymentRequests
            )
        );
        setMerchantPaymentConfirmationEmailCcForEdit(
            buildPaymentRequestEmailCcEditorState(
                organization.merchantPaymentConfirmationEmailCc
            )
        );
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
        setIsEditingConfig(true);
    };

    const commitPaymentRequestEmailCcInput = (): void => {
        const trimmedInput = merchantPaymentConfirmationEmailCcInput.trim();
        if (trimmedInput.length === 0) {
            setMerchantPaymentConfirmationEmailCcInput('');
            setMerchantPaymentConfirmationEmailCcInputError(null);
            return;
        }
        const parseResult = PaymentRequestEmailCcEntrySchema.safeParse(trimmedInput);
        if (!parseResult.success) {
            setMerchantPaymentConfirmationEmailCcInputError(
                parseResult.error.issues[0]?.message ?? 'Enter a valid email address'
            );
            return;
        }
        const normalizedEmail = normalizePaymentRequestEmailCcValue(parseResult.data);
        setMerchantPaymentConfirmationEmailCcForEdit((currentEmails) => {
            if (currentEmails.includes(normalizedEmail)) {
                return currentEmails;
            }
            return [...currentEmails, normalizedEmail];
        });
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
    };

    const handleSaveConfig = () => {
        const isDealertrackEnabledForEdit: boolean = enabledIpwSystemIdsForEdit.includes(
            IpwSystemId.DEALERTRACK
        );
        try {
            const customerPortalPaymentRequestsPayload =
                buildCustomerPortalPaymentRequestsPayloadOrThrow(
                    customerPortalPaymentRequestsForEdit
                );
            setConfigEditorError(null);
            updateConfigMutation.mutate({
                enable_ipg_system_ids: enabledIpgSystemIdsForEdit,
                enable_ipw_system_ids: enabledIpwSystemIdsForEdit,
                dealertrackReconciliation: isDealertrackEnabledForEdit
                    ? dealertrackReconciliationForEdit
                    : undefined,
                merchantPaymentMethods: merchantPaymentMethodsForEdit,
                customerPortalPaymentRequests: customerPortalPaymentRequestsPayload,
                merchantPaymentConfirmationEmailCc:
                    merchantPaymentConfirmationEmailCcForEdit,
            });
        } catch (error: unknown) {
            const errorMessage: string = error instanceof Error ? error.message : String(error);
            log.error('Invalid merchant configuration editor state', errorMessage);
            setConfigEditorError(errorMessage);
        }
    };

    const handleCancelConfig = (organization: GetOrganizationResponse | OrganizationListItem) => {
        setConfigEditorError(null);
        setEnabledIpgSystemIdsForEdit(organization.enable_ipg_system_ids ?? []);
        setEnabledIpwSystemIdsForEdit(organization.enable_ipw_system_ids ?? []);
        setDealertrackReconciliationForEdit(
            buildDealertrackReconciliationEditorState(organization.dealertrackReconciliation)
        );
        setMerchantPaymentMethodsForEdit(
            buildMerchantPaymentMethodsEditorState(organization.merchantPaymentMethods)
        );
        setCustomerPortalPaymentRequestsForEdit(
            buildCustomerPortalPaymentRequestsEditorState(
                organization.customerPortalPaymentRequests
            )
        );
        setMerchantPaymentConfirmationEmailCcForEdit(
            buildPaymentRequestEmailCcEditorState(
                organization.merchantPaymentConfirmationEmailCc
            )
        );
        setMerchantPaymentConfirmationEmailCcInput('');
        setMerchantPaymentConfirmationEmailCcInputError(null);
        setIsEditingConfig(false);
    };

    const handleEditBusinessDetails = (
        organization: GetOrganizationResponse | OrganizationListItem
    ): void => {
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(organization.businessDetails));
        setIsEditingBusinessDetails(true);
    };

    const handleCancelBusinessDetails = (
        organization: GetOrganizationResponse | OrganizationListItem
    ): void => {
        setBusinessDetailsEditorError(null);
        setBusinessDetailsEditorState(buildBusinessDetailsEditorState(organization.businessDetails));
        setIsEditingBusinessDetails(false);
    };

    const handleSaveBusinessDetails = (): void => {
        try {
            const businessDetailsPayload: BusinessDetails =
                buildBusinessDetailsPayloadOrThrow(businessDetailsEditorState);
            setBusinessDetailsEditorError(null);
            updateBusinessDetailsMutation.mutate(businessDetailsPayload);
        } catch (error: unknown) {
            const errorMessage: string = error instanceof Error ? error.message : String(error);
            log.error('Invalid business details editor state', errorMessage);
            setBusinessDetailsEditorError(errorMessage);
        }
    };

    const isBusinessDetailsSaveEnabled: boolean = useMemo((): boolean => {
        try {
            buildBusinessDetailsPayloadOrThrow(businessDetailsEditorState);
            return true;
        } catch {
            return false;
        }
    }, [businessDetailsEditorState]);

    if (isLoading) {
        return <div>Loading organization...</div>;
    }

    if (error || !org) {
        return <div className="text-red-600">Error loading organization: {String(error)}</div>;
    }

    const organizationView: OrganizationView = getOrganizationView(
        org,
        searchParams.get('view')
    );
    const orgIsIso: boolean = isIsoOrganization(org);
    const orgIsMerchant: boolean = isMerchantOrganization(org);
    const parentIsIso: boolean = isIsoOrganization(parentOrganizationData);
    const parentIsMerchant: boolean = isMerchantOrganization(parentOrganizationData);
    const ipgSystemOptions: ReadonlyArray<MerchantSystemOption<IpgSystemId>> = getIpgSystemOptions();
    const ipwSystemOptions: ReadonlyArray<MerchantSystemOption<IpwSystemId>> = getIpwSystemOptions();

    const onSubmit = (data: EditOrgFormData) => {
        updateMutation.mutate(data);
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (org?.status === 'DELETED') {
            undeleteMutation.mutate();
        } else {
            deleteMutation.mutate();
        }
    };

    const handleOrganizationUsersImportFileSelected = async (file: File): Promise<void> => {
        const csvText = await file.text();
        setOrganizationUsersImportCsvText(csvText);
        setOrganizationUsersImportFileName(file.name);
        setOrganizationUsersImportError(null);
        await previewOrganizationUsersCsvImportMutation.mutateAsync(csvText);
    };

    const toggleOrganizationUsersImportRowSelection = (rowIndex: number): void => {
        setSelectedOrganizationUsersImportRowIndexes((currentRowIndexes) =>
            currentRowIndexes.includes(rowIndex)
                ? currentRowIndexes.filter((currentRowIndex) => currentRowIndex !== rowIndex)
                : [...currentRowIndexes, rowIndex].sort((left, right) => left - right)
        );
    };

    const handleToggleAllOrganizationUsersImportRows = (checked: boolean): void => {
        setSelectedOrganizationUsersImportRowIndexes(
            checked ? actionableOrganizationUsersImportRowIndexes : []
        );
    };

    const beginAssignParentOrganizationFromItem: (
        parentOrg: OrganizationOmnisearchListItem,
        parentOrgType: ParentOrganizationType
    ) => void = (
        parentOrg: OrganizationOmnisearchListItem,
        parentOrgType: ParentOrganizationType
    ) => {
        if (organizationUuid === undefined) {
            throw new Error('Organization UUID is required');
        }
        if (parentOrg.organization_uuid === organizationUuid) {
            throw new Error('Organization cannot be its own parent');
        }
        setPendingParentOrganizationUuid(parentOrg.organization_uuid);
        setPendingParentOrganizationName(parentOrg.name);
        setPendingParentOrganizationType(parentOrgType);
        setPendingParentAction('assign');
        setShowReassignConfirm(true);
    };

    const beginRevokeParentOrganization: (
        currentParentOrgName: string,
        currentParentOrgType: ParentOrganizationType
    ) => void = (
        currentParentOrgName: string,
        currentParentOrgType: ParentOrganizationType
    ) => {
        setPendingParentOrganizationUuid(null);
        setPendingParentOrganizationName(currentParentOrgName);
        setPendingParentOrganizationType(currentParentOrgType);
        setPendingParentAction('revoke');
        setShowReassignConfirm(true);
    };

    const handleConfirmReassign: () => void = () => {
        if (pendingParentAction === null) {
            throw new Error('No pending parent organization action to confirm');
        }

        if (pendingParentAction === 'revoke') {
            setParentOrganizationMutation.mutate(null);
            setPendingParentAction(null);
            return;
        }

        if (pendingParentOrganizationUuid === null) {
            throw new Error('Missing pending parent organization UUID for assignment');
        }

        setParentOrganizationMutation.mutate(pendingParentOrganizationUuid);
        setPendingParentAction(null);
    };

    const buildParentOrganizationConfirmationMessage: () => string = () => {
        if (pendingParentAction === null) {
            throw new Error('Missing pending parent action for confirmation message');
        }
        if (pendingParentOrganizationName === null || pendingParentOrganizationType === null) {
            throw new Error('Missing pending parent organization details for confirmation message');
        }

        if (pendingParentAction === 'revoke') {
            if (orgIsMerchant && pendingParentOrganizationType === 'ISO') {
                return `Revoking the assignment of ${org.name} to ISO ${pendingParentOrganizationName}.`;
            }
            if (orgIsIso && pendingParentOrganizationType === 'ISO') {
                return `Revoking the assignment of ${org.name} to parent ISO ${pendingParentOrganizationName}.`;
            }
            if (orgIsMerchant && pendingParentOrganizationType === 'MERCHANT') {
                return `Revoking the assignment of ${org.name} to parent merchant ${pendingParentOrganizationName}.`;
            }
            throw new Error(
                `Unsupported revoke combination: organization=${getOrganizationCapacityLabel(org)}, parent.type=${pendingParentOrganizationType}`
            );
        }

        if (pendingParentAction === 'assign') {
            if (orgIsMerchant && pendingParentOrganizationType === 'ISO') {
                return `Assign ${org.name} to ISO ${pendingParentOrganizationName}?`;
            }
            if (orgIsIso && pendingParentOrganizationType === 'ISO') {
                return `Assign ${org.name} to parent ISO ${pendingParentOrganizationName}?`;
            }
            if (orgIsMerchant && pendingParentOrganizationType === 'MERCHANT') {
                return `Assign ${org.name} to parent merchant ${pendingParentOrganizationName}?`;
            }
            throw new Error(
                `Unsupported assign combination: organization=${getOrganizationCapacityLabel(org)}, parent.type=${pendingParentOrganizationType}`
            );
        }

        throw new Error(`Unknown pendingParentAction: ${pendingParentAction}`);
    };

    const parentOrganizationReassignConfirmationState: ParentOrganizationReassignConfirmationState =
        {
            showReassignConfirm,
            pendingParentAction,
            pendingParentOrganizationName,
            pendingParentOrganizationType,
        };
    const isParentOrganizationReassignConfirmationModalOpen: boolean =
        getIsParentOrganizationReassignConfirmationModalOpen(
            parentOrganizationReassignConfirmationState
        );
    const isInternalAdminRole: boolean = activeRoleAssignment?.role === 'INTERNAL_ADMIN';
    const isSuperadminRole: boolean = activeRoleAssignment?.role === 'SUPERADMIN';
    const canRenderMerchantBackupCard: boolean =
        orgIsMerchant && (isInternalAdminRole || isSuperadminRole);
    const isMerchantSubsidiary: boolean =
        org.parent_organization_uuid !== null && parentIsMerchant;
    const merchantBackupButtonTooltip: string =
        isMerchantSubsidiary ? 'Cannot export a subsidiary' : '';
    const isTopLevelMerchantOrganization: boolean = orgIsMerchant && !isMerchantSubsidiary;
    const businessDetailsSummary: string =
        org.businessDetails === undefined
            ? 'No business details configured.'
            : `${org.businessDetails.country} | ${org.businessDetails.legalEntityName}`;
    const isMainPhoneMissingForReceipt: boolean =
        org.businessDetails === undefined || isBlankOptionalField(org.businessDetails.main_phone_number);
    const isStreetMissingForReceipt: boolean =
        org.businessDetails === undefined ||
        isBlankOptionalField(org.businessDetails.address?.street);
    const isCityMissingForReceipt: boolean =
        org.businessDetails === undefined || isBlankOptionalField(org.businessDetails.address?.city);
    const isUsStateMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'US'
            ? isBlankOptionalField(org.businessDetails.address?.state)
            : false;
    const isUsZipMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'US'
            ? isBlankOptionalField(org.businessDetails.address?.zip)
            : false;
    const isCaProvinceMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'CA'
            ? isBlankOptionalField(org.businessDetails.address?.province)
            : false;
    const isCaPostalCodeMissingForReceipt: boolean =
        org.businessDetails !== undefined && org.businessDetails.country === 'CA'
            ? isBlankOptionalField(org.businessDetails.address?.postal_code)
            : false;
    const organizationConfigurationTitle: string =
        organizationView === 'MERCHANT' ? 'Merchant Configuration' : 'ISO configuration';
    const merchantAccountsForOrganization: ReadonlyArray<MerchantAccountSearchListItem> =
        merchantAccountsForOrganizationQuery.data === undefined
            ? []
            : merchantAccountsForOrganizationQuery.data;

    return (
        <div>
            <BackButton
                href={`/organizations?view=${searchParams.get('view') ?? 'iso'}`}
                tooltip="Back to Organizations"
            />

            <div className="flex justify-between items-start mb-6">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-3xl font-bold">{org.name}</h1>
                        <StatusBadge status={org.status} />
                    </div>
                    {orgIsMerchant && (
                        <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
                            {merchantAccountsForOrganizationQuery.isLoading && (
                                <p>Loading merchant accounts...</p>
                            )}
                            {merchantAccountsForOrganizationQuery.error !== null && (
                                <p className="text-destructive">
                                    Failed to load merchant accounts.
                                </p>
                            )}
                            {!merchantAccountsForOrganizationQuery.isLoading &&
                                merchantAccountsForOrganizationQuery.error === null &&
                                merchantAccountsForOrganization.length === 0 && (
                                    <p>No merchant accounts</p>
                                )}
                            {!merchantAccountsForOrganizationQuery.isLoading &&
                                merchantAccountsForOrganizationQuery.error === null &&
                                merchantAccountsForOrganization.map((merchantAccount) => (
                                    <button
                                        key={merchantAccount.merchant_account_uuid}
                                        type="button"
                                        className="block hover:underline"
                                        onClick={() => {
                                            runInBackground(
                                                navigate(
                                                    `/payments-infrastructure/merchant-accounts/${merchantAccount.merchant_account_uuid}`
                                                ),
                                                (err) => {
                                                    log.error(
                                                        'Error navigating to merchant account detail page:',
                                                        err
                                                    );
                                                }
                                            );
                                        }}
                                    >
                                        {formatMerchantAccountSummaryLine(merchantAccount)}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    {isTopLevelMerchantOrganization && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        `/organizations/${organizationUuidFromRoute}/merchant-checklist`
                                    ),
                                    (err) => {
                                        log.error('Error navigating to merchant checklist page:', err);
                                    }
                                );
                            }}
                        >
                            Open Checklist
                        </Button>
                    )}
                    {!isEditing && org.status !== 'DELETED' && (
                        <Button
                            onClick={() => {
                                setIsEditing(true);
                            }}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {deleteMutation.error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
                    Error deleting organization: {String(deleteMutation.error)}
                </div>
            )}

            {isEditing && (
                <Card id="business-details-card" className="mb-6">
                    <CardHeader>
                        <CardTitle>Edit Organization</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <Input id="name" {...register('name')} />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="status">Status</Label>
                                <select
                                    id="status"
                                    {...register('status')}
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="SUSPENDED">Suspended</option>
                                    <option value="DELETED">Deleted</option>
                                </select>
                                {errors.status && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.status.message}
                                    </p>
                                )}
                            </div>

                            {updateMutation.error && (
                                <div className="text-red-600 text-sm">
                                    Error: {String(updateMutation.error)}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Button type="submit" disabled={updateMutation.isPending}>
                                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        reset();
                                        setIsEditing(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Organization Configuration Card */}
            {orgIsMerchant && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Business Details</CardTitle>
                        {canManageOrganizations && org.status !== 'DELETED' && (
                            <Button
                                size="sm"
                                variant={isEditingBusinessDetails ? 'outline' : 'default'}
                                onClick={() => {
                                    if (isEditingBusinessDetails) {
                                        handleCancelBusinessDetails(org);
                                    } else {
                                        handleEditBusinessDetails(org);
                                    }
                                }}
                            >
                                {isEditingBusinessDetails ? 'Cancel' : 'Edit'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {!isEditingBusinessDetails ? (
                            org.businessDetails === undefined ? (
                                <p className="text-sm text-muted-foreground">
                                    No business details configured.
                                </p>
                            ) : (
                                <div className="space-y-1">
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Country:</span>{' '}
                                        {org.businessDetails.country}
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Legal entity:</span>{' '}
                                        {org.businessDetails.legalEntityName}
                                    </div>
                                    {org.businessDetails.dba !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">DBA:</span>{' '}
                                            {org.businessDetails.dba}
                                        </div>
                                    )}
                                    {org.businessDetails.department !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">Department:</span>{' '}
                                            {org.businessDetails.department}
                                        </div>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'Main phone:',
                                                isMainPhoneMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(
                                            org.businessDetails.main_phone_number
                                        )}
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'Street:',
                                                isStreetMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(
                                            org.businessDetails.address?.street
                                        )}
                                    </div>
                                    {org.businessDetails.address?.street2 !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground">Street 2:</span>{' '}
                                            {org.businessDetails.address.street2}
                                        </div>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">
                                            {renderOptionalReceiptFieldLabel(
                                                'City:',
                                                isCityMissingForReceipt
                                            )}
                                        </span>{' '}
                                        {formatOptionalFieldValue(org.businessDetails.address?.city)}
                                    </div>
                                    {org.businessDetails.country === 'US' && (
                                        <>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'State:',
                                                        isUsStateMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.state
                                                )}
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'ZIP:',
                                                        isUsZipMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.zip
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {org.businessDetails.country === 'CA' && (
                                        <>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'Province:',
                                                        isCaProvinceMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.province
                                                )}
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">
                                                    {renderOptionalReceiptFieldLabel(
                                                        'Postal code:',
                                                        isCaPostalCodeMissingForReceipt
                                                    )}
                                                </span>{' '}
                                                {formatOptionalFieldValue(
                                                    org.businessDetails.address?.postal_code
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Timezone:</span>{' '}
                                        {getOrganizationTimezoneDisplayValue(org)}
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <Label htmlFor="business_details_country">Country</Label>
                                    <select
                                        id="business_details_country"
                                        className="w-full border rounded px-3 py-2"
                                        value={businessDetailsEditorState.country}
                                        onChange={(event) => {
                                            const selectedCountry: 'US' | 'CA' =
                                                event.target.value === 'CA' ? 'CA' : 'US';
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                country: selectedCountry,
                                            });
                                        }}
                                    >
                                        <option value="US">US</option>
                                        <option value="CA">CA</option>
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="business_details_legal_entity_name">
                                        Legal entity name
                                    </Label>
                                    <Input
                                        id="business_details_legal_entity_name"
                                        value={businessDetailsEditorState.legalEntityName}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                legalEntityName: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_dba">DBA</Label>
                                    <Input
                                        id="business_details_dba"
                                        value={businessDetailsEditorState.dba}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                dba: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_department">Department</Label>
                                    <Input
                                        id="business_details_department"
                                        value={businessDetailsEditorState.department}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                department: event.target.value,
                                            });
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="business_details_main_phone_number">
                                        {renderOptionalReceiptFieldLabel(
                                            'Main phone number',
                                            normalizePhoneNumber(
                                                businessDetailsEditorState.mainPhoneNumber
                                            ).length === 0
                                        )}
                                    </Label>
                                    <Input
                                        id="business_details_main_phone_number"
                                        value={businessDetailsEditorState.mainPhoneNumber}
                                        onChange={(event) => {
                                            setBusinessDetailsEditorState({
                                                ...businessDetailsEditorState,
                                                mainPhoneNumber: formatPhoneNumber(event.target.value),
                                            });
                                        }}
                                    />
                                </div>
                                {businessDetailsEditorState.country === 'US' && (
                                    <div className="space-y-3 rounded border p-3">
                                        <div>
                                            <Label htmlFor="business_details_street">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Street',
                                                    businessDetailsEditorState.street.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_street"
                                                value={businessDetailsEditorState.street}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_street2">Street 2</Label>
                                            <Input
                                                id="business_details_street2"
                                                value={businessDetailsEditorState.street2}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street2: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_city">
                                                {renderOptionalReceiptFieldLabel(
                                                    'City',
                                                    businessDetailsEditorState.city.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_city"
                                                value={businessDetailsEditorState.city}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        city: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_state">
                                                {renderOptionalReceiptFieldLabel(
                                                    'State',
                                                    businessDetailsEditorState.usState === ''
                                                )}
                                            </Label>
                                            <select
                                                id="business_details_state"
                                                className="w-full border rounded px-3 py-2"
                                                value={businessDetailsEditorState.usState}
                                                onChange={(event) => {
                                                    const selectedStateCandidate: USState | '' | undefined =
                                                        event.target.value === ''
                                                            ? ''
                                                            : undefined;
                                                    if (selectedStateCandidate === '') {
                                                        setBusinessDetailsEditorState({
                                                            ...businessDetailsEditorState,
                                                            usState: selectedStateCandidate,
                                                        });
                                                        return;
                                                    }
                                                    const selectedUsStateCandidate: USState | undefined =
                                                        USStateKeyList.find(
                                                            (usStateKey) =>
                                                                usStateKey === event.target.value
                                                        );
                                                    if (selectedUsStateCandidate === undefined) {
                                                        throw new Error(
                                                            `Invalid US state selected: ${event.target.value}`
                                                        );
                                                    }
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        usState: selectedUsStateCandidate,
                                                    });
                                                }}
                                            >
                                                <option value="">Unknown</option>
                                                {USStateKeyList.map((usState) => (
                                                    <option key={usState} value={usState}>
                                                        {usState}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_zip">
                                                {renderOptionalReceiptFieldLabel(
                                                    'ZIP',
                                                    businessDetailsEditorState.usZip.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_zip"
                                                value={businessDetailsEditorState.usZip}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        usZip: normalizeUSZipCode(event.target.value),
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {businessDetailsEditorState.country === 'CA' && (
                                    <div className="space-y-3 rounded border p-3">
                                        <div>
                                            <Label htmlFor="business_details_street">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Street',
                                                    businessDetailsEditorState.street.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_street"
                                                value={businessDetailsEditorState.street}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_street2">Street 2</Label>
                                            <Input
                                                id="business_details_street2"
                                                value={businessDetailsEditorState.street2}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        street2: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_city">
                                                {renderOptionalReceiptFieldLabel(
                                                    'City',
                                                    businessDetailsEditorState.city.trim().length ===
                                                        0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_city"
                                                value={businessDetailsEditorState.city}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        city: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_ca_province">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Province',
                                                    businessDetailsEditorState.caProvince === ''
                                                )}
                                            </Label>
                                            <select
                                                id="business_details_ca_province"
                                                className="w-full border rounded px-3 py-2"
                                                value={businessDetailsEditorState.caProvince}
                                                onChange={(event) => {
                                                    const selectedProvinceCandidate:
                                                        | CanadaProvince
                                                        | ''
                                                        | undefined =
                                                        event.target.value === '' ? '' : undefined;
                                                    if (selectedProvinceCandidate === '') {
                                                        setBusinessDetailsEditorState({
                                                            ...businessDetailsEditorState,
                                                            caProvince: selectedProvinceCandidate,
                                                        });
                                                        return;
                                                    }
                                                    const selectedCaProvinceCandidate:
                                                        | CanadaProvince
                                                        | undefined = CanadaProvinceKeyList.find(
                                                        (caProvinceKey) =>
                                                            caProvinceKey === event.target.value
                                                    );
                                                    if (selectedCaProvinceCandidate === undefined) {
                                                        throw new Error(
                                                            `Invalid Canada province selected: ${event.target.value}`
                                                        );
                                                    }
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        caProvince: selectedCaProvinceCandidate,
                                                    });
                                                }}
                                            >
                                                <option value="">Unknown</option>
                                                {CanadaProvinceKeyList.map((caProvince) => (
                                                    <option key={caProvince} value={caProvince}>
                                                        {caProvince}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="business_details_ca_postal_code">
                                                {renderOptionalReceiptFieldLabel(
                                                    'Postal code',
                                                    businessDetailsEditorState.caPostalCode.trim()
                                                        .length === 0
                                                )}
                                            </Label>
                                            <Input
                                                id="business_details_ca_postal_code"
                                                value={businessDetailsEditorState.caPostalCode}
                                                onChange={(event) => {
                                                    setBusinessDetailsEditorState({
                                                        ...businessDetailsEditorState,
                                                        caPostalCode: normalizeCanadaPostalCode(
                                                            event.target.value
                                                        ),
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="text-xs text-muted-foreground">
                                    {businessDetailsSummary}
                                </div>
                                {businessDetailsEditorError !== null && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{businessDetailsEditorError}</AlertDescription>
                                    </Alert>
                                )}
                                {updateBusinessDetailsMutation.error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {String(updateBusinessDetailsMutation.error)}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={handleSaveBusinessDetails}
                                        disabled={
                                            updateBusinessDetailsMutation.isPending ||
                                            !isBusinessDetailsSaveEnabled
                                        }
                                    >
                                        {updateBusinessDetailsMutation.isPending
                                            ? 'Saving...'
                                            : 'Save'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleCancelBusinessDetails(org)}
                                        disabled={updateBusinessDetailsMutation.isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {orgIsMerchant && canEditConfig && (
                <Card id="merchant-configuration-card" className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{organizationConfigurationTitle}</CardTitle>
                        {!isEditingConfig && (
                            <Button onClick={() => handleEditConfig(org)} size="sm">
                                Edit
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {isEditingConfig ? (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="enable_ipg_system_ids_config">Enabled gateways</Label>
                                    <select
                                        id="enable_ipg_system_ids_config"
                                        className="w-full border rounded px-3 py-2"
                                        value=""
                                        onChange={(event) => {
                                            const selectedIpgOption = ipgSystemOptions.find(
                                                (option) =>
                                                    String(option.systemId) === event.target.value
                                            );
                                            if (selectedIpgOption === undefined) {
                                                throw new Error(
                                                    `Unknown selected gateway id: ${event.target.value}`
                                                );
                                            }
                                            setEnabledIpgSystemIdsForEdit(
                                                appendUniqueSystemId(
                                                    enabledIpgSystemIdsForEdit,
                                                    selectedIpgOption.systemId
                                                )
                                            );
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select gateway to add...
                                        </option>
                                        {ipgSystemOptions.map((ipgOption) => (
                                            <option
                                                key={ipgOption.systemId}
                                                value={String(ipgOption.systemId)}
                                            >
                                                {ipgOption.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex flex-wrap gap-2">
                                        {enabledIpgSystemIdsForEdit.map((ipgSystemId) => (
                                            <Badge
                                                key={ipgSystemId}
                                                variant="secondary"
                                                className="gap-2 pr-1"
                                            >
                                                {getSystemOptionLabelOrThrow(
                                                    ipgSystemOptions,
                                                    ipgSystemId
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove gateway ${ipgSystemId}`}
                                                    className="rounded px-1 hover:bg-muted-foreground/20"
                                                    onClick={() => {
                                                        setEnabledIpgSystemIdsForEdit(
                                                            removeSystemId(
                                                                enabledIpgSystemIdsForEdit,
                                                                ipgSystemId
                                                            )
                                                        );
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="enable_ipw_system_ids_config">Enabled websites</Label>
                                    <select
                                        id="enable_ipw_system_ids_config"
                                        className="w-full border rounded px-3 py-2"
                                        value=""
                                        onChange={(event) => {
                                            const selectedIpwOption = ipwSystemOptions.find(
                                                (option) =>
                                                    String(option.systemId) === event.target.value
                                            );
                                            if (selectedIpwOption === undefined) {
                                                throw new Error(
                                                    `Unknown selected website id: ${event.target.value}`
                                                );
                                            }
                                            setEnabledIpwSystemIdsForEdit(
                                                appendUniqueSystemId(
                                                    enabledIpwSystemIdsForEdit,
                                                    selectedIpwOption.systemId
                                                )
                                            );
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select website to add...
                                        </option>
                                        {ipwSystemOptions.map((ipwOption) => (
                                            <option
                                                key={ipwOption.systemId}
                                                value={String(ipwOption.systemId)}
                                            >
                                                {ipwOption.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex flex-wrap gap-2">
                                        {enabledIpwSystemIdsForEdit.map((ipwSystemId) => (
                                            <Badge
                                                key={ipwSystemId}
                                                variant="secondary"
                                                className="gap-2 pr-1"
                                            >
                                                {getSystemOptionLabelOrThrow(
                                                    ipwSystemOptions,
                                                    ipwSystemId
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove website ${ipwSystemId}`}
                                                    className="rounded px-1 hover:bg-muted-foreground/20"
                                                    onClick={() => {
                                                        setEnabledIpwSystemIdsForEdit(
                                                            removeSystemId(
                                                                enabledIpwSystemIdsForEdit,
                                                                ipwSystemId
                                                            )
                                                        );
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                {enabledIpwSystemIdsForEdit.includes(IpwSystemId.DEALERTRACK) && (
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Dealertrack reconciliation
                                        </p>
                                        <div className="space-y-2">
                                            <Label htmlFor="dealertrack_payment_code">
                                                Payment code
                                            </Label>
                                            <Input
                                                id="dealertrack_payment_code"
                                                value={dealertrackReconciliationForEdit.paymentCode}
                                                onChange={(event) => {
                                                    setDealertrackReconciliationForEdit({
                                                        ...dealertrackReconciliationForEdit,
                                                        paymentCode: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    dealertrackReconciliationForEdit.doInjectApprovalNumber
                                                }
                                                onChange={(event) => {
                                                    setDealertrackReconciliationForEdit({
                                                        ...dealertrackReconciliationForEdit,
                                                        doInjectApprovalNumber: event.target.checked,
                                                    });
                                                }}
                                            />
                                            Inject merchant transaction reference
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    dealertrackReconciliationForEdit.doFinalizeReconciliation
                                                }
                                                onChange={(event) => {
                                                    setDealertrackReconciliationForEdit({
                                                        ...dealertrackReconciliationForEdit,
                                                        doFinalizeReconciliation: event.target.checked,
                                                    });
                                                }}
                                            />
                                            Finalize reconciliation
                                        </label>
                                    </div>
                                )}
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">Payment methods</p>
                                    <label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={merchantPaymentMethodsForEdit.terminal}
                                            onCheckedChange={(checked) => {
                                                setMerchantPaymentMethodsForEdit({
                                                    ...merchantPaymentMethodsForEdit,
                                                    terminal: checked === true,
                                                });
                                            }}
                                        />
                                        Terminal
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={merchantPaymentMethodsForEdit.card}
                                            onCheckedChange={(checked) => {
                                                setMerchantPaymentMethodsForEdit({
                                                    ...merchantPaymentMethodsForEdit,
                                                    card: checked === true,
                                                });
                                            }}
                                        />
                                        Card
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={merchantPaymentMethodsForEdit.ach}
                                            onCheckedChange={(checked) => {
                                                setMerchantPaymentMethodsForEdit({
                                                    ...merchantPaymentMethodsForEdit,
                                                    ach: checked === true,
                                                });
                                            }}
                                        />
                                        ACH
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={merchantPaymentMethodsForEdit.e_invoice}
                                            onCheckedChange={(checked) => {
                                                setMerchantPaymentMethodsForEdit({
                                                    ...merchantPaymentMethodsForEdit,
                                                    e_invoice: checked === true,
                                                });
                                            }}
                                        />
                                        E-Invoice
                                    </label>
                                </div>
                                <div className="space-y-3 rounded-md border p-3">
                                    <p className="text-sm font-medium">
                                        Customer portal payment requests
                                    </p>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={customerPortalPaymentRequestsForEdit.enabled}
                                            onChange={(event) => {
                                                setCustomerPortalPaymentRequestsForEdit({
                                                    ...customerPortalPaymentRequestsForEdit,
                                                    enabled: event.target.checked,
                                                });
                                            }}
                                        />
                                        Enable payment requests
                                    </label>
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Allowed delivery channels</p>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={customerPortalPaymentRequestsForEdit.allowEmail}
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        allowEmail: event.target.checked,
                                                    });
                                                }}
                                            />
                                            Email
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={customerPortalPaymentRequestsForEdit.allowSms}
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        allowSms: event.target.checked,
                                                    });
                                                }}
                                            />
                                            SMS
                                        </label>
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">Twilio SMS sender</p>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={customerPortalPaymentRequestsForEdit.twilioEnabled}
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        twilioEnabled: event.target.checked,
                                                    });
                                                }}
                                            />
                                            Enable Twilio SMS delivery
                                        </label>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_twilio_sender_mode">
                                                Sender routing
                                            </Label>
                                            <select
                                                id="customer_portal_twilio_sender_mode"
                                                className="w-full border rounded px-3 py-2"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.twilioSenderMode
                                                }
                                                onChange={(event) => {
                                                    const nextMode: TwilioSenderMode =
                                                        event.target.value === 'from_phone_number'
                                                            ? 'from_phone_number'
                                                            : 'messaging_service_sid';
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        twilioSenderMode: nextMode,
                                                    });
                                                }}
                                            >
                                                <option value="messaging_service_sid">
                                                    Messaging Service SID
                                                </option>
                                                <option value="from_phone_number">
                                                    From phone number
                                                </option>
                                            </select>
                                        </div>
                                        {customerPortalPaymentRequestsForEdit.twilioSenderMode ===
                                        'from_phone_number' ? (
                                            <div className="space-y-2">
                                                <Label htmlFor="customer_portal_twilio_from_phone">
                                                    From phone number
                                                </Label>
                                                    <Input
                                                        id="customer_portal_twilio_from_phone"
                                                        value={
                                                            customerPortalPaymentRequestsForEdit.twilioFromPhoneNumber
                                                        }
                                                        onChange={(event) => {
                                                            setCustomerPortalPaymentRequestsForEdit({
                                                                ...customerPortalPaymentRequestsForEdit,
                                                                twilioFromPhoneNumber:
                                                                    formatPhoneNumber(
                                                                        event.target.value
                                                                    ),
                                                            });
                                                        }}
                                                    />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <Label htmlFor="customer_portal_twilio_messaging_service_sid">
                                                    Messaging Service SID
                                                </Label>
                                                <Input
                                                    id="customer_portal_twilio_messaging_service_sid"
                                                    value={
                                                        customerPortalPaymentRequestsForEdit.twilioMessagingServiceSid
                                                    }
                                                    onChange={(event) => {
                                                        setCustomerPortalPaymentRequestsForEdit({
                                                            ...customerPortalPaymentRequestsForEdit,
                                                            twilioMessagingServiceSid:
                                                                event.target.value,
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">SendGrid email sender</p>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    customerPortalPaymentRequestsForEdit.sendgridEnabled
                                                }
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridEnabled: event.target.checked,
                                                    });
                                                }}
                                            />
                                            Enable SendGrid email delivery
                                        </label>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_from_email">
                                                From email
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_from_email"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridFromEmail
                                                }
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridFromEmail: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_from_name">
                                                From name
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_from_name"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridFromName
                                                }
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridFromName: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="customer_portal_sendgrid_reply_to_email">
                                                Reply-to email
                                            </Label>
                                            <Input
                                                id="customer_portal_sendgrid_reply_to_email"
                                                value={
                                                    customerPortalPaymentRequestsForEdit.sendgridReplyToEmail
                                                }
                                                onChange={(event) => {
                                                    setCustomerPortalPaymentRequestsForEdit({
                                                        ...customerPortalPaymentRequestsForEdit,
                                                        sendgridReplyToEmail: event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-3 rounded-md border p-3">
                                        <p className="text-sm font-medium">
                                            Merchant payment confirmation CC emails
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {merchantPaymentConfirmationEmailCcForEdit.map((email) => (
                                                <Badge
                                                    key={email}
                                                    variant="secondary"
                                                    className="gap-2 pr-1"
                                                >
                                                    {email}
                                                    <button
                                                        type="button"
                                                        aria-label={`Remove CC email ${email}`}
                                                        className="rounded px-1 hover:bg-muted-foreground/20"
                                                        onClick={() => {
                                                            setMerchantPaymentConfirmationEmailCcForEdit(
                                                                merchantPaymentConfirmationEmailCcForEdit.filter(
                                                                    (currentEmail) =>
                                                                        currentEmail !== email
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        x
                                                    </button>
                                                </Badge>
                                            ))}
                                            {merchantPaymentConfirmationEmailCcForEdit.length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No CC emails configured
                                                </p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="payment_request_email_cc_input">
                                                Add CC email
                                            </Label>
                                            <Input
                                                id="payment_request_email_cc_input"
                                                value={merchantPaymentConfirmationEmailCcInput}
                                                onChange={(event) => {
                                                    setMerchantPaymentConfirmationEmailCcInput(
                                                        event.target.value
                                                    );
                                                    setMerchantPaymentConfirmationEmailCcInputError(
                                                        null
                                                    );
                                                }}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === 'Tab'
                                                    ) {
                                                        event.preventDefault();
                                                        commitPaymentRequestEmailCcInput();
                                                    }
                                                }}
                                            />
                                            {merchantPaymentConfirmationEmailCcInputError !== null && (
                                                <p className="text-sm text-destructive">
                                                    {merchantPaymentConfirmationEmailCcInputError}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {configEditorError !== null && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{configEditorError}</AlertDescription>
                                    </Alert>
                                )}
                                {updateConfigMutation.error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {updateConfigMutation.error instanceof Error
                                                ? updateConfigMutation.error.message
                                                : 'Failed to update configuration'}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleSaveConfig}
                                        disabled={updateConfigMutation.isPending}
                                    >
                                        {updateConfigMutation.isPending ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button variant="outline" onClick={() => handleCancelConfig(org)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Enabled gateways</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(org.enable_ipg_system_ids ?? []).map(
                                                (ipgSystemId: IpgSystemId) => (
                                                    <Badge key={ipgSystemId} variant="secondary">
                                                        {getSystemOptionLabelOrThrow(
                                                            ipgSystemOptions,
                                                            ipgSystemId
                                                        )}
                                                    </Badge>
                                                )
                                            )}
                                            {(org.enable_ipg_system_ids ?? []).length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No gateways enabled
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Enabled websites</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(org.enable_ipw_system_ids ?? []).map(
                                                (ipwSystemId: IpwSystemId) => (
                                                    <Badge key={ipwSystemId} variant="secondary">
                                                        {getSystemOptionLabelOrThrow(
                                                            ipwSystemOptions,
                                                            ipwSystemId
                                                        )}
                                                    </Badge>
                                                )
                                            )}
                                            {(org.enable_ipw_system_ids ?? []).length === 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    No websites enabled
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {(org.enable_ipw_system_ids ?? []).includes(
                                        IpwSystemId.DEALERTRACK
                                    ) && (
                                        <div className="space-y-2">
                                            <p className="text-sm font-medium">
                                                Dealertrack reconciliation
                                            </p>
                                            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
                                                <p>
                                                    Payment code:{' '}
                                                    {org.dealertrackReconciliation?.paymentCode ?? '-'}
                                                </p>
                                                <p>
                                                    Inject merchant transaction reference:{' '}
                                                    {org.dealertrackReconciliation?.doInjectApprovalNumber
                                                        ? 'Yes'
                                                        : 'No'}
                                                </p>
                                                <p>
                                                    Finalize reconciliation:{' '}
                                                    {org.dealertrackReconciliation?.doFinalizeReconciliation
                                                        ? 'Yes'
                                                        : 'No'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">Payment methods</p>
                                        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                            <p>
                                                Terminal:{' '}
                                                {org.merchantPaymentMethods?.terminal ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                Card:{' '}
                                                {org.merchantPaymentMethods?.card ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                ACH:{' '}
                                                {org.merchantPaymentMethods?.ach ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                E-Invoice:{' '}
                                                {org.merchantPaymentMethods?.e_invoice ? 'Yes' : 'No'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium">
                                            Customer portal payment requests
                                        </p>
                                        <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                            <p>
                                                Enabled:{' '}
                                                {org.customerPortalPaymentRequests?.enabled
                                                    ? 'Yes'
                                                    : 'No'}
                                            </p>
                                            <p>
                                                Allowed delivery channels:{' '}
                                                {(org.customerPortalPaymentRequests?.allowedDeliveryChannels
                                                    ?.length ?? 0) > 0
                                                    ? org.customerPortalPaymentRequests?.allowedDeliveryChannels.join(
                                                          ', '
                                                      )
                                                    : 'None'}
                                            </p>
                                            <p>
                                                Twilio SMS sender:{' '}
                                                {org.customerPortalPaymentRequests?.twilioSms ===
                                                undefined
                                                    ? 'Not configured'
                                                    : 'fromPhoneNumber' in
                                                          org.customerPortalPaymentRequests.twilioSms
                                                      ? formatPhoneNumber(
                                                            org.customerPortalPaymentRequests
                                                                .twilioSms.fromPhoneNumber ?? ''
                                                        )
                                                      : org.customerPortalPaymentRequests.twilioSms
                                                            .messagingServiceSid}
                                            </p>
                                            <p>
                                                SendGrid from email:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.fromEmail ?? 'Not configured'}
                                            </p>
                                            <p>
                                                SendGrid from name:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.fromName ?? 'Not configured'}
                                            </p>
                                            <p>
                                                SendGrid reply-to email:{' '}
                                                {org.customerPortalPaymentRequests?.sendgridEmail
                                                    ?.replyToEmail ?? 'Not configured'}
                                            </p>
                                            <div className="space-y-2 md:col-span-2">
                                                <p>Merchant payment confirmation CC emails:</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {(org.merchantPaymentConfirmationEmailCc ?? [])
                                                        .length > 0 ? (
                                                        (org.merchantPaymentConfirmationEmailCc ?? []).map(
                                                            (email) => (
                                                                <Badge
                                                                    key={email}
                                                                    variant="secondary"
                                                                >
                                                                    {email}
                                                                </Badge>
                                                            )
                                                        )
                                                    ) : (
                                                        <p className="text-sm text-muted-foreground">
                                                            None
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {orgIsMerchant && canEditConfig && (
                <Card id="document-templates-card" className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Document Templates</CardTitle>
                        <Button
                            size="sm"
                            onClick={() =>
                                navigate(
                                    `/organizations/${organizationUuidFromRoute}/document-templates`
                                )
                            }
                        >
                            Details
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            Manage registry-backed document template overrides on a dedicated
                            page.
                        </p>
                    </CardContent>
                </Card>
            )}


            {(orgIsIso || orgIsMerchant) && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>
                            {organizationView === 'ISO'
                                ? 'Client Merchants'
                                : 'Subsidiary Merchants'}
                        </CardTitle>
                        {canManageOrganizations && (
                            <Button
                                onClick={() => {
                                    const createMerchantUrl =
                                        organizationView === 'ISO'
                                            ? `/organizations/create/merchant?parent_iso_uuid=${org.organization_uuid}`
                                            : `/organizations/create/merchant?parent_merchant_uuid=${org.organization_uuid}`;
                                    runInBackground(navigate(createMerchantUrl), (err) => {
                                        log.error('Error navigating to create merchant:', err);
                                    });
                                }}
                                size="sm"
                            >
                                Create Merchant
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <EmbeddedInfiniteScrollList<
                            OrganizationOmnisearchListItem,
                            string,
                            readonly unknown[]
                        >
                            queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                'organization',
                                organizationUuidFromRoute,
                                'subsidiary_merchants',
                            ])}
                            pageSize={50}
                            maxHeight="50rem"
                            enabled={orgIsIso || orgIsMerchant}
                            parseCursorParam={parseEmbeddedListCursorParam}
                            fetchPage={async ({ limit, cursor }) => {
                                if (organizationUuid === undefined) {
                                    throw new Error('Organization UUID is required');
                                }
                                const response = await organizationApi.listSubsidiaryMerchantsPage(
                                    organizationUuid,
                                    {
                                        limit,
                                        cursor,
                                    }
                                );
                                return {
                                    items: response.merchants,
                                    pagination: response.pagination,
                                };
                            }}
                            stateCopy={{
                                emptyLabel:
                                    organizationView === 'ISO'
                                        ? 'No merchants assigned to this ISO.'
                                        : 'No subsidiary merchants.',
                            }}
                            renderItems={({ items }) => (
                                <div className="space-y-2">
                                    {items.map((merchant) => (
                                        <div
                                            key={merchant.organization_uuid}
                                            className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                            onClick={() => {
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${merchant.organization_uuid}?view=merchant`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            <div>
                                                <div className="font-medium">{merchant.name}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    UUID: {merchant.organization_uuid}
                                                </div>
                                            </div>
                                            <StatusBadge status={merchant.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Secrets Card */}
            {orgIsMerchant && (
                <SecretsCard
                    organizationUuid={org.organization_uuid}
                    organizationName={org.name}
                />
            )}

            {/* Permissions Policies */}
            {canManageOrganizations && (
                <PolicyPermissionBundlesCard
                    organizationUuid={org.organization_uuid}
                    organizationName={formatOrganizationDisplayName(org)}
                />
            )}

            {canAuditPermissions && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Permissions Audit</CardTitle>
                        <Button
                            size="sm"
                            onClick={() =>
                                navigate(
                                    `/organizations/${organizationUuidFromRoute}/permissions-audit`
                                )
                            }
                        >
                            Details
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            Audit persisted permission grants for users in this
                            organization tree.
                        </p>
                    </CardContent>
                </Card>
            )}

            {(orgIsIso || orgIsMerchant) && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>
                            {organizationView === 'ISO'
                                ? 'Parent ISO'
                                : parentIsMerchant
                                  ? 'Parent Merchant'
                                  : 'ISO of record'}
                        </CardTitle>
                        {orgIsIso && canManageOrganizations && (
                            <Button
                                size="sm"
                                variant={isReassigning ? 'outline' : 'default'}
                                onClick={() => setIsReassigning(!isReassigning)}
                            >
                                {isReassigning ? 'Cancel' : 'Assign Parent ISO'}
                            </Button>
                        )}
                        {orgIsMerchant &&
                            canManageOrganizations &&
                            parentOrganizationData !== undefined &&
                            parentIsMerchant && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        beginRevokeParentOrganization(
                                            parentOrganizationData.name,
                                            'MERCHANT'
                                        );
                                    }}
                                >
                                    Revoke
                                </Button>
                            )}
                        {orgIsMerchant &&
                            canManageOrganizations &&
                            (org.parent_organization_uuid === null ||
                                !parentIsMerchant) && (
                                <Button
                                    size="sm"
                                    variant={isReassigning ? 'outline' : 'default'}
                                    onClick={() => {
                                        setIsAssigningParentMerchant(false);
                                        setIsReassigning(!isReassigning);
                                    }}
                                >
                                    {isReassigning ? 'Cancel' : 'Assign to ISO'}
                                </Button>
                            )}
                    </CardHeader>
                    <CardContent>
                        {org.parent_organization_uuid ? (
                            parentOrganizationData ? (
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="font-medium">
                                            {parentOrganizationData.name}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                const parentView =
                                                    parentIsMerchant
                                                        ? 'merchant'
                                                        : 'iso';
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${parentOrganizationData.organization_uuid}?view=${parentView}`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            View
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Loading parent organization...
                                </p>
                            )
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {organizationView === 'ISO'
                                    ? 'No parent ISO'
                                    : 'Not assigned to any ISO'}
                            </p>
                        )}

                        {orgIsMerchant &&
                            canManageOrganizations &&
                            !parentIsMerchant &&
                            isReassigning && (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between gap-2">
                                        <Label>ISO of record</Label>
                                        {org.parent_organization_uuid !== null &&
                                            parentOrganizationData !== undefined &&
                                            parentIsIso && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        beginRevokeParentOrganization(
                                                            parentOrganizationData.name,
                                                            'ISO'
                                                        );
                                                    }}
                                                >
                                                    Revoke
                                                </Button>
                                            )}
                                    </div>
                                    <div className="mt-2">
                                        <OmnisearchSelect<
                                            OrganizationOmnisearchListItem,
                                            string
                                        >
                                            label="Search ISOs"
                                            placeholder="Type 3+ characters..."
                                            pageSize={25}
                                            disabled={setParentOrganizationMutation.isPending}
                                            selectedItemId={null}
                                            fetchPage={async (params) => {
                                                const response =
                                                    await organizationApi.searchOrganizations({
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                        is_iso: true,
                                                    });
                                                return {
                                            items: response.organizations,
                                            pagination: response.pagination,
                                        };
                                    }}
                                            getItemId={(item) => item.organization_uuid}
                                            getItemLabel={(item) =>
                                                `${item.name} (${getOrganizationCapacityLabel(item)})`
                                            }
                                            getItemFilterText={(item) =>
                                                `${item.name} ${item.organization_uuid}`
                                            }
                                            renderItemCard={renderOrganizationOmnisearchCard}
                                            onSelect={(item) => {
                                                if (!organizationHasCapacity(item, 'is_iso')) {
                                                    throw new Error(
                                                        `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                    );
                                                }
                                                beginAssignParentOrganizationFromItem(item, 'ISO');
                                            }}
                                        />
                                    </div>
                                    {setParentOrganizationMutation.error && (
                                        <div className="text-red-600 text-sm">
                                            Error: {String(setParentOrganizationMutation.error)}
                                        </div>
                                    )}
                                </div>
                            )}

                        {orgIsIso && canManageOrganizations && isReassigning && (
                            <div className="mt-4">
                                <div className="flex items-center justify-between gap-2">
                                    <Label>Parent ISO</Label>
                                    {org.parent_organization_uuid !== null &&
                                        parentOrganizationData !== undefined &&
                                        parentIsIso && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    beginRevokeParentOrganization(
                                                        parentOrganizationData.name,
                                                        'ISO'
                                                    );
                                                }}
                                            >
                                                Revoke
                                            </Button>
                                        )}
                                </div>
                                <div className="mt-2">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search parent ISOs"
                                        placeholder="Type 3+ characters..."
                                        pageSize={25}
                                        disabled={setParentOrganizationMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_iso: true,
                                                });
                                            return {
                                                items: response.organizations.filter(
                                                    (candidate) =>
                                                        candidate.organization_uuid !==
                                                        organizationUuid
                                                ),
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        renderItemCard={renderOrganizationOmnisearchCard}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_iso')) {
                                                throw new Error(
                                                    `Expected ISO candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            beginAssignParentOrganizationFromItem(item, 'ISO');
                                        }}
                                    />
                                </div>
                                {setParentOrganizationMutation.error && (
                                    <div className="text-red-600 text-sm">
                                        Error: {String(setParentOrganizationMutation.error)}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {orgIsMerchant && org.parent_organization_uuid === null && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Parent Merchant</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                size="sm"
                                variant={isAssigningParentMerchant ? 'outline' : 'default'}
                                onClick={() => {
                                    setIsReassigning(false);
                                    setIsAssigningParentMerchant(!isAssigningParentMerchant);
                                }}
                            >
                                {isAssigningParentMerchant ? 'Cancel' : 'Assign Parent Merchant'}
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div>
                            <p className="text-sm text-muted-foreground">No parent merchant</p>

                            {!canManageOrganizations && (
                                <p className="text-sm text-muted-foreground mt-2">
                                    Access denied. Insufficient permissions.
                                </p>
                            )}

                            {canManageOrganizations && isAssigningParentMerchant && (
                                <div className="mt-4">
                                    <OmnisearchSelect<
                                        OrganizationOmnisearchListItem,
                                        string
                                    >
                                        label="Search parent merchants"
                                        placeholder="Type 3+ characters..."
                                        pageSize={25}
                                        disabled={setParentOrganizationMutation.isPending}
                                        selectedItemId={null}
                                        fetchPage={async (params) => {
                                            const response =
                                                await organizationApi.searchOrganizations({
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                    is_merchant: true,
                                                });
                                            return {
                                                items: response.organizations.filter(
                                                    (candidate) =>
                                                        candidate.organization_uuid !==
                                                        organizationUuid
                                                ),
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) =>
                                            `${item.name} (${getOrganizationCapacityLabel(item)})`
                                        }
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`
                                        }
                                        renderItemCard={renderOrganizationOmnisearchCard}
                                        onSelect={(item) => {
                                            if (!organizationHasCapacity(item, 'is_merchant')) {
                                                throw new Error(
                                                    `Expected MERCHANT candidate, got ${getOrganizationCapacityLabel(item)}`
                                                );
                                            }
                                            beginAssignParentOrganizationFromItem(item, 'MERCHANT');
                                        }}
                                    />
                                    {setParentOrganizationMutation.error && (
                                        <div className="text-red-600 text-sm mt-2">
                                            Error: {String(setParentOrganizationMutation.error)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {orgIsIso && (
                <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Subsidiary ISOs</CardTitle>
                        {canManageOrganizations && (
                            <Button
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/organizations/create/iso?parent_iso_uuid=${org.organization_uuid}`
                                        ),
                                        (err) => {
                                            log.error('Error navigating to create ISO:', err);
                                        }
                                    );
                                }}
                                size="sm"
                            >
                                Create ISO
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <EmbeddedInfiniteScrollList<
                            OrganizationOmnisearchListItem,
                            string,
                            readonly unknown[]
                        >
                            queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                                'organization',
                                organizationUuidFromRoute,
                                'subsidiary_isos',
                            ])}
                            pageSize={50}
                            maxHeight="50rem"
                            enabled={orgIsIso}
                            parseCursorParam={parseEmbeddedListCursorParam}
                            fetchPage={async ({ limit, cursor }) => {
                                if (organizationUuid === undefined) {
                                    throw new Error('Organization UUID is required');
                                }
                                const response = await organizationApi.listSubsidiaryIsosPage(
                                    organizationUuid,
                                    {
                                        limit,
                                        cursor,
                                    }
                                );
                                return {
                                    items: response.isos,
                                    pagination: response.pagination,
                                };
                            }}
                            stateCopy={{
                                emptyLabel: 'No subsidiary ISOs.',
                            }}
                            renderItems={({ items }) => (
                                <div className="space-y-2">
                                    {items.map((iso) => (
                                        <div
                                            key={iso.organization_uuid}
                                            className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                            onClick={() => {
                                                runInBackground(
                                                    navigate(
                                                        `/organizations/${iso.organization_uuid}?view=iso`
                                                    ),
                                                    (err) => {
                                                        log.error(
                                                            'Error navigating to organization:',
                                                            err
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            <div>
                                                <div className="font-medium">{iso.name}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    UUID: {iso.organization_uuid}
                                                </div>
                                            </div>
                                            <StatusBadge status={iso.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    </CardContent>
                </Card>
            )}


            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Organization Members</CardTitle>
                    {org.status !== 'DELETED' && (
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowImportUsersDialog(true);
                                    setOrganizationUsersImportError(null);
                                }}
                            >
                                Import Users
                            </Button>
                            <Button
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/users/create?organization_uuid=${organizationUuidFromRoute}`
                                        ),
                                        (err) => {
                                            log.error('Error navigating to create user:', err);
                                        }
                                    );
                                }}
                                size="sm"
                            >
                                Create User
                            </Button>
                        </div>
                    )}
                </CardHeader>
                <CardContent>
                    <EmbeddedInfiniteScrollList<
                        UserProfileDetailedPublicResponse,
                        string,
                        readonly unknown[]
                    >
                        queryKeyPrefix={buildRoleScopedQueryKey(activeRoleAssignmentId, [
                            'organization',
                            organizationUuidFromRoute,
                            'users',
                        ])}
                        pageSize={50}
                        maxHeight="50rem"
                        enabled={true}
                        parseCursorParam={parseEmbeddedListCursorParam}
                        fetchPage={async ({ limit, cursor }) => {
                            if (organizationUuid === undefined) {
                                throw new Error('Organization UUID is required');
                            }
                            const response = await organizationApi.listDirectOrganizationUsersPage(
                                organizationUuid,
                                {
                                    limit,
                                    cursor,
                                }
                            );
                            return {
                                items: response.users,
                                pagination: response.pagination,
                            };
                        }}
                        stateCopy={{
                            emptyLabel: 'No users assigned to this organization.',
                        }}
                        renderItems={({ items }) => (
                            <div className="space-y-2">
                                {items.map((user) => (
                                    <div
                                        key={user.user_uuid}
                                        className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                        onClick={() => {
                                            runInBackground(navigate(`/users/${user.user_uuid}`), (err) => {
                                                log.error('Error navigating to user:', err);
                                            });
                                        }}
                                    >
                                        <div>
                                            <div className="font-medium">{user.primary_email}</div>
                                            <div className="text-sm text-muted-foreground">
                                                Roles:{' '}
                                                {getAllRoleAssignments(user)
                                                        .filter(
                                                            (roleAssignment) =>
                                                                roleAssignment.organization_uuid ===
                                                                organizationUuid
                                                        )
                                                    .map((roleAssignment) => roleAssignment.role)
                                                    .join(', ')}
                                            </div>
                                        </div>
                                        <StatusBadge status={user.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                </CardContent>
            </Card>

            <Dialog
                open={showImportUsersDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        resetOrganizationUsersImportDialog();
                        return;
                    }
                    setShowImportUsersDialog(true);
                }}
            >
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Import Users</DialogTitle>
                        <DialogDescription>
                            Upload a CSV file to preview inserts, updates, no-op rows, and
                            non-actionable errors before saving.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {organizationUsersImportError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>{organizationUsersImportError}</AlertDescription>
                            </Alert>
                        ) : null}

                        {organizationUsersImportPreview === null ? (
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="organization-users-import-file">
                                        Upload CSV
                                    </Label>
                                    <Input
                                        id="organization-users-import-file"
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(event) => {
                                            const file = event.target.files?.[0];
                                            if (file === undefined) {
                                                return;
                                            }
                                            runInBackground(
                                                handleOrganizationUsersImportFileSelected(file),
                                                (err) => {
                                                    log.error(
                                                        'Error previewing imported organization users:',
                                                        err
                                                    );
                                                }
                                            );
                                        }}
                                    />
                                </div>
                                {organizationUsersImportFileName !== null ? (
                                    <div className="text-sm text-muted-foreground">
                                        Selected file: {organizationUsersImportFileName}
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="text-sm text-muted-foreground">
                                        {organizationUsersImportFileName ?? 'Uploaded CSV'}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            checked={areAllActionableOrganizationUsersImportRowsSelected}
                                            disabled={
                                                actionableOrganizationUsersImportRowIndexes.length === 0
                                            }
                                            onCheckedChange={(checked) => {
                                                handleToggleAllOrganizationUsersImportRows(
                                                    checked === true
                                                );
                                            }}
                                        />
                                        <span className="text-sm">Select all</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {organizationUsersImportPreview.rows.map((row) => {
                                        const isActionable =
                                            isActionableOrganizationUserCsvImportRow(row);
                                        return (
                                            <div
                                                key={row.row_index}
                                                className="rounded-md border p-4 space-y-3"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-center gap-3">
                                                        {isActionable ? (
                                                            <Checkbox
                                                                checked={selectedOrganizationUsersImportRowIndexSet.has(
                                                                    row.row_index
                                                                )}
                                                                onCheckedChange={() => {
                                                                    toggleOrganizationUsersImportRowSelection(
                                                                        row.row_index
                                                                    );
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="w-4" />
                                                        )}
                                                        <div>
                                                            <div className="font-medium">
                                                                Row {row.row_index}
                                                            </div>
                                                            <div className="text-sm text-muted-foreground">
                                                                {row.row_data.primary_email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Badge variant="secondary">
                                                        {getOrganizationUserCsvImportClassificationLabel(
                                                            row
                                                        )}
                                                    </Badge>
                                                </div>
                                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Name
                                                        </div>
                                                        <div>
                                                            {[
                                                                row.row_data.first_name,
                                                                row.row_data.last_name,
                                                            ]
                                                                .filter(
                                                                    (value): value is string =>
                                                                        value !== undefined
                                                                )
                                                                .join(' ') || '—'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Role
                                                        </div>
                                                        <div>{row.row_data.role}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Policies
                                                        </div>
                                                        <div>
                                                            {row.row_data.policies.join(', ') || '—'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">
                                                            Phones
                                                        </div>
                                                        <div>
                                                            {row.row_data.phones.join(', ') || '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                                {row.existing_user !== undefined ? (
                                                    <div className="text-sm text-muted-foreground">
                                                        Existing user: {row.existing_user.primary_email}
                                                        {' · '}
                                                        {row.existing_user.role}
                                                    </div>
                                                ) : null}
                                                {row.conflicting_users !== undefined ? (
                                                    <div className="text-sm text-red-600">
                                                        Conflicts with:{' '}
                                                        {row.conflicting_users
                                                            .map((userRow) => userRow.primary_email)
                                                            .join(', ')}
                                                    </div>
                                                ) : null}
                                                {(row.row_errors?.length ?? 0) > 0 ? (
                                                    <div className="space-y-1 text-sm text-red-600">
                                                        {row.row_errors?.map((rowError) => (
                                                            <div key={rowError}>{rowError}</div>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        {organizationUsersImportPreview !== null ? (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setOrganizationUsersImportPreview(null);
                                    setSelectedOrganizationUsersImportRowIndexes([]);
                                    setOrganizationUsersImportError(null);
                                }}
                            >
                                Choose Another CSV
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                resetOrganizationUsersImportDialog();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={
                                organizationUsersImportPreview === null
                                || selectedOrganizationUsersImportRowIndexes.length === 0
                                || applyOrganizationUsersCsvImportMutation.isPending
                            }
                            onClick={() => {
                                applyOrganizationUsersCsvImportMutation.mutate(
                                    selectedOrganizationUsersImportRowIndexes
                                );
                            }}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {org.is_customer === true && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Payments</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-3">
                        <Button
                            type="button"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-requests',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to pending payments:', err);
                                    }
                                );
                            }}
                        >
                            Pending Payments
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-history',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to recent payments:', err);
                                    }
                                );
                            }}
                        >
                            Recent Payments
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            '/customer/payment-methods',
                                            org.organization_uuid
                                        )
                                    ),
                                    (err) => {
                                        log.error('Error navigating to payment methods:', err);
                                    }
                                );
                            }}
                        >
                            Payment Methods
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Delete/Restore Organization Card - Only for superadmins */}
            {hasSuperadminPrivileges(activeRoleAssignment ?? null) && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>
                            {org.status === 'DELETED'
                                ? 'Restore Organization'
                                : 'Delete Organization'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                                {org.status === 'DELETED'
                                    ? 'Restore this organization.'
                                    : 'Delete this organization.'}
                            </p>
                            <Button
                                variant={org.status === 'DELETED' ? 'default' : 'destructive'}
                                onClick={handleDelete}
                                disabled={deleteMutation.isPending || undeleteMutation.isPending}
                            >
                                {deleteMutation.isPending || undeleteMutation.isPending
                                    ? org.status === 'DELETED'
                                        ? 'Restoring...'
                                        : 'Deleting...'
                                    : org.status === 'DELETED'
                                      ? 'Undelete'
                                      : 'Delete'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Delete/Restore Organization Confirmation Modal */}
            {org && (
                <ConfirmationModal
                    open={showDeleteConfirm}
                    onOpenChange={setShowDeleteConfirm}
                    message={
                        org.status === 'DELETED'
                            ? `Are you sure you want to undelete ${org.name}?`
                            : `Are you sure you want to delete ${org.name}?`
                    }
                    onConfirm={handleConfirmDelete}
                    confirmLabel={org.status === 'DELETED' ? 'Undelete' : 'Delete'}
                    isLoading={deleteMutation.isPending || undeleteMutation.isPending}
                />
            )}

            {/* Reassign Organization Confirmation Modal */}
            {org && (
                <ConfirmationModal
                    open={isParentOrganizationReassignConfirmationModalOpen}
                    onOpenChange={(open: boolean) => {
                        setShowReassignConfirm(open);
                    }}
                    message={
                        isParentOrganizationReassignConfirmationModalOpen
                            ? buildParentOrganizationConfirmationMessage()
                            : ''
                    }
                    onConfirm={handleConfirmReassign}
                    confirmLabel={pendingParentAction === 'revoke' ? 'Revoke' : 'Assign'}
                    isLoading={setParentOrganizationMutation.isPending}
                />
            )}

            {canRenderMerchantBackupCard && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Merchant Backup</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Download a JSON backup for this merchant organization.
                        </p>
                        <div title={merchantBackupButtonTooltip} className="w-fit">
                            <Button
                                onClick={() => {
                                    exportMerchantBackupMutation.mutate();
                                }}
                                disabled={
                                    exportMerchantBackupMutation.isPending || isMerchantSubsidiary
                                }
                            >
                                {exportMerchantBackupMutation.isPending
                                    ? 'Preparing backup...'
                                    : 'Download JSON Backup'}
                            </Button>
                        </div>
                        {exportMerchantBackupMutation.error !== null && (
                            <p className="text-sm text-destructive">
                                {String(exportMerchantBackupMutation.error)}
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
